// Dashboard page — the retailer's landing screen.
//
// Spec: /root/TKOS_MASTER_CONTEXT.md §Dashboard + inventory_sections/01_retailer.md
// Source-of-truth render lives in portal-standalone.html ~1993-2505 (#page-dashboard).
//
// Backend integration
// -------------------
// Hydrates from `GET /api/portal/dashboard?retailerId=...` — the kitchenos-api
// aggregator at modules/portal_dashboard.js (added 2026-05-11). The aggregator
// returns a `sections` envelope where each tile carries its own { ok, error };
// per-section failures fall back to demo for that field only, rest of the
// page still renders live.
//
// Sections currently wired live → DashView fields:
//   messages     → cards.messages.value, messages[]
//   leads        → cards.submissions.value, pipeline.leads
//   offers       → offers[]
//   appointments → cards.events.value, schedule[]
//   bookmarks    → myLinks[]
//   deliveries   → cards.deliveries.value (active deliveries, PG)
// Sections still demo (no backing data in v1 aggregator): activity,
//   attention, reviews, stdLinks, and pipeline stages beyond leads.
//
// localStorage usage
// ------------------
// Quick Notes  → tkos.dashboard.quicknotes (free-text)
// To-Do List   → tkos.dashboard.todos      (JSON array of {id,text,done})
// These are the ONLY two localStorage keys this page reads/writes — the rest
// of the dashboard state is in-memory only. Both are explicitly called out as
// allowed UI-state preferences in the page spec.
//
// Drag-and-drop
// -------------
// Drag-drop layout reorder was intentionally removed from TKOS — the legacy
// Sortable.js implementation is gone, the Reset Layout button is gone, and
// no drag handles are reproduced here. Do not reintroduce.
//
// Live clock
// ----------
// 1-second `setInterval` updates HH:MM:SS + the date label. Pauses on
// `visibilitychange → hidden` to keep the tab quiet when backgrounded, resumes
// + paints immediately on `visible`. Cleared in the cleanup function.

import { ApiError, api } from "@tkos/api-client";
import { alertDialog, createModal, iconMarkup, promptDialog, sanitiseRichHtml, type IconName } from "@tkos/shared-ui";
import { openEventDetail } from "../../shell/event-detail-modal";
import { openNewCustomerModal } from "../../shell/new-customer-modal";
import { openSpotlight } from "../../shell/spotlight";
import {
  getPresets as getRadioPresets,
  getRadioState,
  searchStations,
  selectPreset,
  selectStation,
  setVolume,
  subscribe as subscribeRadio,
  toggle as toggleRadio,
  type RadioState,
  type SearchHit,
} from "../../shell/radio";
import { registerPage } from "../../shell/router";
import * as realtime from "../../shell/realtime";
import { applyRoleSoon } from "../../shell/role-gate";
import { demoDataAllowed } from "../../shell/onboarding";
import type { Page, PageContext, PageCleanup } from "../../shell/types";
import { mountKitBar } from "./kit-bar";
import { mountMessagesWidget } from "./messages-widget";
import { mountActivityWidget } from "./activity-widget";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

interface SparklineSeries {
  /** 7-point series, lower index = older. Auto-scaled in render. */
  points: number[];
  /** Stroke colour for the polyline. */
  color: string;
}

interface StatCard {
  key: string;
  label: string;
  value: number;
  icon: IconName;
  spark: SparklineSeries;
  /** Optional click-target page name (passed to ctx.navigate). */
  navTo?: string;
}

type PipelineStage = "lead" | "qualified" | "quoted" | "won" | "lost";

interface PipelineCounts {
  lead: number;
  qualified: number;
  quoted: number;
  won: number;
  lost: number;
}

interface RevenueBlock {
  thisMonthGbp: number;
  lastMonthGbp: number;
  ytdGbp: number;
  targetGbp: number;
  /** 6-month bar chart values, oldest first. */
  trail: { label: string; value: number }[];
}

interface MessagePreview {
  id: string;
  customer: string;
  channel: "email" | "whatsapp" | "phone" | "sms" | "in-app";
  preview: string;
  at: string; // ISO
}

interface ScheduleItem {
  id: string;
  time: string; // "10:00"
  title: string;
  where: string;
  tone: "blue" | "green" | "orange" | "purple";
  // Optional richer fields surfaced into the click-through detail popup.
  date?: string;     // YYYY-MM-DD
  customer?: string;
  type?: string;
  notes?: string;
}

interface ActivityEvent {
  icon: IconName;
  text: string;
  at: string; // ISO
  tone: "ok" | "info" | "warn" | "danger" | "money";
}

interface AttentionItem {
  id: string;
  text: string;
  priority: "high" | "medium" | "low";
  done: boolean;
}

interface SupplierOffer {
  id: string;
  supplierId: string;
  supplierName: string;
  supplierColor: string;
  type: "account-holder" | "new-customer";
  title: string;
  description: string;
  /** Sanitised HTML body — server already strips scripts/iframes/handlers. */
  contentHtml: string;
  badge: string;
  cta: string;
  ctaUrl: string;
  validUntil: string | null;
}

/** Supplier-published news item — surfaces in the Supplier News widget.
 *  Same store as offers (`/api/supplier-offers-news`), `kind === "news"`. */
interface SupplierNewsItem {
  id: string;
  supplierId: string;
  supplierName: string;
  supplierColor: string;
  headline: string;
  summary: string;
  contentHtml: string;
  thumbnail: string | null;
  publishedAt: number | null;
}

interface GoogleReviewBlock {
  rating: number; // 0-5
  count: number;
  reviews: { name: string; rating: number; date: string; text: string }[];
}

interface MyLink {
  name: string;
  domain: string;
  url: string;
  initial: string;
  bg: string;
  fg?: string;
}

interface StdLinkCategory {
  key: string;
  label: string;
  items: { name: string; domain: string; url: string }[];
}

/** Live Courier Tracking widget — inbound deliveries from major UK couriers. */
type CourierCode = "dpd" | "dhl" | "royalmail" | "parcelforce" | "apc" | "evri";
type CourierStatus = "collected" | "in_transit" | "out_for_delivery" | "delivered";

interface CourierDelivery {
  id: string;
  ref: string;
  customer: string;
  tracking: string;
  status: CourierStatus;
  etaDays: number;
  note: string;
}

/** Social AI widget — image-drop posting helper. */
interface SocialAiState {
  /** Top posting times by audience engagement, hh:mm 24h. */
  bestTimes: string[];
  /** Suggested hashtags relevant to kitchen retail. */
  hashtags: string[];
}

interface DashView {
  greeting: string;
  ownerName: string;
  cards: StatCard[];
  pipeline: PipelineCounts;
  revenue: RevenueBlock;
  messages: MessagePreview[];
  schedule: ScheduleItem[];
  activity: ActivityEvent[];
  attention: AttentionItem[];
  offers: SupplierOffer[];
  news: SupplierNewsItem[];
  reviews: GoogleReviewBlock;
  myLinks: MyLink[];
  stdLinks: StdLinkCategory[];
  couriers: CourierDelivery[];
  socialAi: SocialAiState;
  isDemo: boolean;
}

// ── Aggregator response types (mirror modules/portal_dashboard.js) ──────────

interface AggregatorSection {
  ok: boolean;
  error?: string;
}

interface AggMessagePreview {
  id?: string;
  threadId?: string;
  from?: string;
  subject?: string;
  snippet?: string;
  createdAt?: string;
}

interface AggOfferPreview {
  id?: string;
  supplierName?: string;
  title?: string;
  type?: string;
  discount?: string;
  validTo?: string | null;
}

interface AggAppointmentPreview {
  id?: string;
  customerName?: string;
  visitType?: string;
  preferredDate?: string;
  preferredTime?: string;
  requestedAt?: string;
}

interface AggBookmark {
  id?: string;
  title?: string;
  url?: string;
  category?: string;
}

interface AggregatorResponse {
  retailerId: string;
  generatedAt: string;
  sections: {
    messages?:     AggregatorSection & { unreadCount?: number; preview?: AggMessagePreview[] };
    leads?:        AggregatorSection & { newCount?: number; totalCount?: number };
    calls?:        AggregatorSection & { recentCount?: number; missedCount?: number };
    offers?:       AggregatorSection & { activeCount?: number; preview?: AggOfferPreview[] };
    appointments?: AggregatorSection & { pendingCount?: number; preview?: AggAppointmentPreview[] };
    customers?:    AggregatorSection & { total?: number; recentCount?: number };
    bookmarks?:    AggregatorSection & { items?: AggBookmark[] };
    revenue?:      AggregatorSection & Partial<RevenueBlock>;
    pipeline?:     AggregatorSection & Partial<PipelineCounts>;
    eventsToday?:  AggregatorSection & { count?: number };
    deliveries?:   AggregatorSection & { count?: number };
  };
}

interface LoadResult {
  view: DashView;
  failedSections: string[];
  /** True when the aggregator wasn't reachable at all (network error, 404/501, or no retailerId). */
  hardFail: boolean;
}

// ── Formatters ───────────────────────────────────────────────────────────────

const GBP_0 = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function clockHHMMSS(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function greetingFor(d: Date): string {
  const h = d.getHours();
  if (h < 5) return "Working late,";
  if (h < 12) return "Good morning,";
  if (h < 18) return "Good afternoon,";
  return "Good evening,";
}

// ── Sparkline (plain SVG <polyline>, auto-scaled) ────────────────────────────

function sparklineSvg(s: SparklineSeries, width = 120, height = 34): string {
  if (s.points.length === 0) return "";
  const min = Math.min(...s.points);
  const max = Math.max(...s.points);
  const span = max - min || 1;
  const stepX = width / Math.max(1, s.points.length - 1);
  const pts = s.points
    .map((v, i) => {
      const x = i * stepX;
      const y = height - 4 - ((v - min) / span) * (height - 8);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return `
    <svg class="dash-spark__svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" aria-hidden="true">
      <polyline fill="none" stroke="${s.color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" points="${pts}"/>
    </svg>
  `;
}

// ── Demo data ────────────────────────────────────────────────────────────────

function demoView(): DashView {
  return {
    greeting: greetingFor(new Date()),
    ownerName: "",
    cards: [
      { key: "submissions", label: "New Submissions", value: 0, icon: "file",
        spark: { color: "#e58200", points: [] }, navTo: "submissions" },
      { key: "messages", label: "Unread Messages", value: 0, icon: "chat",
        spark: { color: "#3b82f6", points: [] }, navTo: "messages" },
      { key: "deliveries", label: "Deliveries", value: 0, icon: "package",
        spark: { color: "#22c55e", points: [] }, navTo: "deliveries" },
      { key: "events", label: "Today's Events", value: 0, icon: "calendar",
        spark: { color: "#a855f7", points: [] }, navTo: "calendar" },
    ],
    pipeline: { lead: 0, qualified: 0, quoted: 0, won: 0, lost: 0 },
    revenue: {
      thisMonthGbp: 0,
      lastMonthGbp: 0,
      ytdGbp: 0,
      targetGbp: 0,
      trail: [],
    },
    messages: [],
    schedule: [],
    activity: [],
    attention: [],
    offers: [],
    news: [],
    reviews: {
      rating: 0,
      count: 0,
      reviews: [],
    },
    myLinks: [
      { name: "Blum e-services",       domain: "blum.com",            url: "https://blum.com",            initial: "B", bg: "#e58200" },
      { name: "Häfele Trade Portal",   domain: "hafele.co.uk",        url: "https://hafele.co.uk",        initial: "H", bg: "#c8102e" },
      { name: "PWS Trade Login",       domain: "pws.co.uk",           url: "https://pws.co.uk",           initial: "P", bg: "#0064a0" },
      { name: "Hettich Product Finder", domain: "hettich.com",         url: "https://hettich.com",         initial: "H", bg: "#ffffff", fg: "#000000" },
      { name: "Uform Trade",           domain: "uform.co.uk",         url: "https://uform.co.uk",         initial: "U", bg: "#1f3a5f" },
      { name: "TKC",                   domain: "tkcomponents.co.uk",  url: "https://tkcomponents.co.uk",  initial: "T", bg: "#444444" },
    ],
    stdLinks: [
      { key: "email", label: "Email", items: [
        { name: "Gmail", domain: "gmail.com", url: "https://mail.google.com" },
        { name: "Outlook", domain: "outlook.com", url: "https://outlook.live.com" },
        { name: "Yahoo Mail", domain: "yahoo.com", url: "https://mail.yahoo.com" },
      ]},
      { key: "messaging", label: "Messaging", items: [
        { name: "Slack", domain: "slack.com", url: "https://slack.com" },
        { name: "Microsoft Teams", domain: "teams.microsoft.com", url: "https://teams.microsoft.com" },
        { name: "Telegram", domain: "telegram.org", url: "https://web.telegram.org" },
        { name: "Signal", domain: "signal.org", url: "https://signal.org" },
        { name: "Discord", domain: "discord.com", url: "https://discord.com" },
      ]},
      { key: "music", label: "Music", items: [
        { name: "Spotify", domain: "spotify.com", url: "https://open.spotify.com" },
        { name: "Apple Music", domain: "music.apple.com", url: "https://music.apple.com" },
        { name: "YouTube Music", domain: "music.youtube.com", url: "https://music.youtube.com" },
        { name: "SoundCloud", domain: "soundcloud.com", url: "https://soundcloud.com" },
      ]},
      { key: "podcasts", label: "Podcasts", items: [
        { name: "BBC Sounds", domain: "bbc.co.uk/sounds", url: "https://www.bbc.co.uk/sounds" },
        { name: "Spotify Podcasts", domain: "spotify.com", url: "https://open.spotify.com/genre/podcasts" },
        { name: "Apple Podcasts", domain: "podcasts.apple.com", url: "https://podcasts.apple.com" },
      ]},
      { key: "inspiration", label: "Inspiration", items: [
        { name: "Houzz", domain: "houzz.co.uk", url: "https://www.houzz.co.uk" },
        { name: "Pinterest", domain: "pinterest.com", url: "https://www.pinterest.com" },
      ]},
      { key: "business", label: "Business", items: [
        { name: "Companies House", domain: "companieshouse.gov.uk",
          url: "https://www.gov.uk/government/organisations/companies-house" },
        { name: "HMRC", domain: "hmrc.gov.uk",
          url: "https://www.gov.uk/government/organisations/hm-revenue-customs" },
        { name: "Xero", domain: "xero.com", url: "https://xero.com" },
        { name: "QuickBooks", domain: "quickbooks.intuit.com", url: "https://quickbooks.intuit.com" },
        { name: "Sage", domain: "sage.com", url: "https://www.sage.com" },
        { name: "Mailchimp", domain: "mailchimp.com", url: "https://mailchimp.com" },
        { name: "Dropbox", domain: "dropbox.com", url: "https://dropbox.com" },
        { name: "Google Drive", domain: "drive.google.com", url: "https://drive.google.com" },
        { name: "Notion", domain: "notion.so", url: "https://notion.so" },
      ]},
      { key: "social", label: "Social Media", items: [
        { name: "Facebook", domain: "facebook.com", url: "https://www.facebook.com" },
        { name: "Instagram", domain: "instagram.com", url: "https://www.instagram.com" },
        { name: "LinkedIn", domain: "linkedin.com", url: "https://www.linkedin.com" },
        { name: "Twitter/X", domain: "x.com", url: "https://x.com" },
      ]},
    ],
    couriers: [],
    socialAi: {
      bestTimes: ["07:45", "12:15", "18:30"],
      hashtags: ["#KitchenDesign", "#KitchensOfInsta", "#TradeKitchen", "#YorkshireKitchens", "#IndependentRetailer"],
    },
    isDemo: true,
  };
}

// ── Backend fetch + translator ───────────────────────────────────────────────

const BOOKMARK_BG_PALETTE = ["#e58200", "#3b82f6", "#22c55e", "#a855f7", "#c8102e", "#0064a0", "#1f3a5f"];

function formatExpires(iso: string | null | undefined): string {
  if (!iso) return "Ongoing";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "Ongoing";
  return new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function domainFromUrl(u: string): string {
  try {
    return new URL(u).hostname.replace(/^www\./, "");
  } catch {
    return u;
  }
}

function bookmarkColor(category: string): string {
  let h = 0;
  for (let i = 0; i < category.length; i++) h = (h * 31 + category.charCodeAt(i)) >>> 0;
  const idx = h % BOOKMARK_BG_PALETTE.length;
  return BOOKMARK_BG_PALETTE[idx] ?? "#3b82f6";
}

function setCardValueByKey(cards: StatCard[], key: string, value: number): StatCard[] {
  return cards.map((c) => (c.key === key ? { ...c, value } : c));
}

/**
 * Translate the aggregator's `sections` envelope into the rendered `DashView`
 * shape, leaving demo values in place for any section the backend doesn't
 * cover or that returned `ok: false`.
 */
function mergeAggregatorIntoView(
  base: DashView,
  agg: AggregatorResponse,
): { view: DashView; failedSections: string[] } {
  const view: DashView = { ...base };
  const failed: string[] = [];
  const s = agg.sections ?? {};

  // Messages → cards.messages + messages[]
  if (s.messages) {
    if (s.messages.ok) {
      view.cards = setCardValueByKey(view.cards, "messages", s.messages.unreadCount ?? 0);
      if (Array.isArray(s.messages.preview) && s.messages.preview.length > 0) {
        view.messages = s.messages.preview.map((m) => ({
          id: String(m.id ?? ""),
          customer: String(m.from ?? "Unknown"),
          channel: "in-app" as const,
          preview: String(m.snippet ?? m.subject ?? ""),
          at: String(m.createdAt ?? new Date().toISOString()),
        }));
      } else {
        view.messages = [];
      }
    } else {
      failed.push("messages");
    }
  }

  // Leads → cards.submissions (New Submissions stat). The Job Pipeline is now
  // driven entirely by the PG `pipeline` section (job_stage), handled below —
  // this file-store count only feeds the submissions stat tile.
  if (s.leads) {
    if (s.leads.ok) {
      const n = s.leads.newCount ?? 0;
      view.cards = setCardValueByKey(view.cards, "submissions", n);
    } else {
      failed.push("leads");
    }
  }

  // Calls — no DashView slot in v1, but track failure for the banner.
  if (s.calls && !s.calls.ok) failed.push("calls");

  // Offers — populated by the dedicated /api/supplier-offers fetch, not the
  // aggregator preview (which lacks colour/badge/cta). Only track failures.
  if (s.offers && !s.offers.ok) failed.push("offers");

  // "Today's Events" counter → actual calendar_events dated today (PG), NOT
  // pending appointment requests (the old, wrong source which ignored the date).
  if (s.eventsToday && s.eventsToday.ok) {
    view.cards = setCardValueByKey(view.cards, "events", s.eventsToday.count ?? 0);
  } else if (s.eventsToday && !s.eventsToday.ok) {
    failed.push("eventsToday");
  }

  // "Deliveries" counter → active deliveries for this retailer (PG). Was the
  // only top-row stat card left hardcoded at 0; now wired to the aggregator.
  if (s.deliveries && s.deliveries.ok) {
    view.cards = setCardValueByKey(view.cards, "deliveries", s.deliveries.count ?? 0);
  } else if (s.deliveries && !s.deliveries.ok) {
    failed.push("deliveries");
  }

  // Appointments → schedule[] (the "Today's Schedule" list)
  if (s.appointments) {
    if (s.appointments.ok) {
      // "Today's Schedule" must only show TODAY's events — the aggregator
      // returns all pending appointments, so filter to today's date here.
      // Event-type strings ("home_visit") are humanised to "Home Visit".
      const today = todayYMDLocal();
      const todaysAppts = Array.isArray(s.appointments.preview)
        ? s.appointments.preview.filter(
            (a) => String(a.preferredDate ?? "") === today,
          )
        : [];
      if (todaysAppts.length > 0) {
        view.schedule = todaysAppts.map((a) => ({
          id: String(a.id ?? ""),
          time: String(a.preferredTime ?? "—"),
          title: `Visit · ${a.customerName ?? "Customer"}`,
          where: humaniseEventLabel(String(a.visitType ?? "")),
          tone: "blue" as const,
          date: String(a.preferredDate ?? ""),
          customer: String(a.customerName ?? ""),
          type: humaniseEventLabel(String(a.visitType ?? "Appointment")),
          notes: "",
        }));
      } else {
        view.schedule = [];
      }
    } else {
      failed.push("appointments");
    }
  }

  // Customers — counts only, no DashView slot. Track failure.
  if (s.customers && !s.customers.ok) failed.push("customers");

  // Bookmarks → myLinks
  if (s.bookmarks) {
    if (s.bookmarks.ok) {
      if (Array.isArray(s.bookmarks.items) && s.bookmarks.items.length > 0) {
        view.myLinks = s.bookmarks.items.map((b) => {
          const title = String(b.title ?? "Link");
          const url = fixWizardLinkHost(String(b.url ?? "#"));
          return {
            name: title,
            domain: domainFromUrl(url),
            url,
            initial: (title[0] ?? "?").toUpperCase(),
            bg: bookmarkColor(String(b.category ?? "")),
          };
        });
      }
    } else {
      failed.push("bookmarks");
    }
  }

  // Pipeline → view.pipeline (Sales Pipeline widget; CRM stages from PG ∪ file store)
  if (s.pipeline) {
    if (s.pipeline.ok) {
      view.pipeline = {
        lead: Number(s.pipeline.lead ?? 0),
        qualified: Number(s.pipeline.qualified ?? 0),
        quoted: Number(s.pipeline.quoted ?? 0),
        won: Number(s.pipeline.won ?? 0),
        lost: Number(s.pipeline.lost ?? 0),
      };
    } else {
      failed.push("pipeline");
    }
  }

  // Revenue → view.revenue (live PG section; Revenue Overview widget)
  if (s.revenue) {
    if (s.revenue.ok) {
      view.revenue = {
        thisMonthGbp: Number(s.revenue.thisMonthGbp ?? 0),
        lastMonthGbp: Number(s.revenue.lastMonthGbp ?? 0),
        ytdGbp: Number(s.revenue.ytdGbp ?? 0),
        targetGbp: Number(s.revenue.targetGbp ?? 0),
        trail: Array.isArray(s.revenue.trail)
          ? s.revenue.trail.map((t) => ({ label: String(t.label ?? ""), value: Number(t.value ?? 0) }))
          : [],
      };
    } else {
      failed.push("revenue");
    }
  }

  view.isDemo = false;
  return { view, failedSections: failed };
}

/**
 * Onboarding guard for the Revenue Overview + Job Pipeline widgets (and the
 * other previously-demo sections). A real retailer account that hasn't finished
 * onboarding shows an empty/zero state rather than figures. Other genuinely-live
 * aggregator sections (messages, schedule, bookmarks) are left untouched.
 *
 * This is the explicit "onboarding_complete check": even though revenue + the
 * pipeline are now live PG data, an un-onboarded account sees them empty.
 */
function enforceEmptyShowcase(view: DashView): void {
  view.revenue = { thisMonthGbp: 0, lastMonthGbp: 0, ytdGbp: 0, targetGbp: 0, trail: [] };
  // Whole pipeline is now PG-sourced (job_stage); a not-yet-onboarded account
  // shows an empty pipeline.
  view.pipeline = { lead: 0, qualified: 0, quoted: 0, won: 0, lost: 0 };
  view.activity = [];
  view.attention = [];
  view.couriers = [];
  view.reviews = { rating: 0, count: 0, reviews: [] };
}

async function loadView(ctx: PageContext, signal: AbortSignal): Promise<LoadResult> {
  const base = demoView();
  const onboarded = demoDataAllowed(ctx);
  const retailerId = ctx.user?.retailerId;
  if (!retailerId) {
    if (!onboarded) enforceEmptyShowcase(base);
    return { view: base, failedSections: [], hardFail: true };
  }
  try {
    const agg = await ctx.api<AggregatorResponse>(
      `/portal/dashboard?retailerId=${encodeURIComponent(retailerId)}`,
      { signal },
    );
    if (signal.aborted) throw new DOMException("aborted", "AbortError");
    const { view, failedSections } = mergeAggregatorIntoView(base, agg);
    if (!onboarded) enforceEmptyShowcase(view);
    return { view, failedSections, hardFail: false };
  } catch (err) {
    if (signal.aborted) throw err;
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      if (!onboarded) enforceEmptyShowcase(base);
      return { view: base, failedSections: [], hardFail: true };
    }
    // Network/500: render empty/zero base so the page still works; caller toasts.
    throw err;
  }
}

// ── SVG icon helpers ─────────────────────────────────────────────────────────

const ICO = {
  search: iconMarkup("search", { size: "sm" }),
  star:   iconMarkup("star", { size: "sm" }),
  play:   iconMarkup("play", { size: "sm" }),
  caret:  iconMarkup("chevron-down", { size: "sm" }),
  upload: iconMarkup("upload", { size: "sm" }),
  truck:  iconMarkup("truck", { size: "sm" }),
  pin:    iconMarkup("pin", { size: "sm" }),
} as const;

// ── Weather ──────────────────────────────────────────────────────────────────

interface WeatherResponse {
  location: string;
  fetchedAt: string;
  current: {
    tempC: number;
    feelsC: number;
    humidity: number;
    isDay: boolean;
    code: number;
    condition: string;
    icon: string;
    windKph: number;
  };
}

function weatherSvg(icon: string): string {
  // Live weather widget — map the API's icon hint onto a TKOS weather icon.
  // Any unknown hint falls back to the plain `cloud` glyph.
  const map: Record<string, IconName> = {
    sun: "sun",
    moon: "moon",
    cloud: "cloud",
    "sun-cloud": "cloud-sun",
    "moon-cloud": "cloud-moon",
    rain: "cloud-rain",
    snow: "cloud-snow",
    fog: "cloud-fog",
    storm: "cloud-bolt",
  };
  return iconMarkup(map[icon] ?? "cloud", { size: "md" });
}


// "Coming Soon" overlay — sits inside a .dash-card--soon widget as a sibling of
// the (still-rendered) content, so the live render functions never wipe it. The
// scrim dims the inert widget while leaving its title legible at the top.
const COMING_SOON = `
  <div class="dash-coming-soon" role="note" aria-label="Coming soon — this feature isn't active yet">
    <span class="dash-coming-soon__badge">
      <svg class="dash-coming-soon__ico" viewBox="0 0 24 24" width="16" height="16" fill="none"
        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9"></circle>
        <path d="M12 7.5V12l3 2"></path>
      </svg>
      Coming Soon
    </span>
    <p class="dash-coming-soon__note">We're putting the finishing touches on this — it'll be live soon.</p>
  </div>`;

// ── Section renderers ────────────────────────────────────────────────────────

function renderShell(container: HTMLElement, view: DashView): void {
  container.innerHTML = `
    <section class="dash-root" aria-label="Dashboard">
      <!-- Header strip -->
      <header class="dash-header">
        <div class="dash-greet">
          <h1 class="dash-greet__title">
            <span data-role="greet-text">${escapeHtml(view.greeting)}</span>
            <span class="dash-greet__name">${escapeHtml(view.ownerName)}</span>
          </h1>
          <div class="dash-greet__date" data-role="date-text">${escapeHtml(DATE_FMT.format(new Date()))}</div>
          <p class="dash-greet__sub">Your trade command centre — manage jobs, suppliers and customers in one place.</p>
        </div>
        <div class="dash-header__right">
          <div class="dash-weather">
            <span class="dash-weather__ico" data-role="weather-icon" aria-hidden="true">
              ${iconMarkup("cloud", { size: "md" })}
            </span>
            <div>
              <div class="dash-weather__temp"><span data-role="weather-temp">--</span>°C</div>
              <div class="dash-weather__cond" data-role="weather-cond">--</div>
            </div>
          </div>
          <div class="dash-clock">
            <div class="dash-clock__time" data-role="clock-time">--:--:--</div>
            <div class="dash-clock__lbl">Local</div>
          </div>
          <form class="dash-search" data-role="search-form" role="search">
            ${ICO.search}
            <input type="search" placeholder="Search the OS…" aria-label="Search the OS" />
          </form>
        </div>
      </header>

      <!-- Demo banner -->
      <div class="dash-banner" data-role="banner" hidden></div>

      <!-- Kit — single compact bar (greeting + category badges + Ask Kit;
           badges expand to that category's cards). -->
      <div class="dash-kit-bar" data-role="kit-bar"></div>

      <!-- Stat cards -->
      <div class="dash-stats" role="list" data-role="stats"></div>

      <!-- Quick actions -->
      <nav class="dash-qa" aria-label="Quick actions" data-role="qa"></nav>

      <!-- Top row: Pipeline + Revenue + Radio -->
      <div class="dash-grid dash-grid--top">
        <section class="dash-card" aria-labelledby="dash-pipeline-title" data-role="pipeline-card" data-perm="crm">
          <header class="dash-card__head">
            <h2 id="dash-pipeline-title" class="dash-card__title">Sales Pipeline</h2>
            <button type="button" class="dash-card__link" data-role="pipeline-all">View all</button>
          </header>
          <div class="dash-pipe" data-role="pipeline-grid"></div>
        </section>

        <section class="dash-card" aria-labelledby="dash-revenue-title" data-perm="analytics">
          <header class="dash-card__head">
            <h2 id="dash-revenue-title" class="dash-card__title">Revenue Overview</h2>
          </header>
          <div class="dash-rev" data-role="revenue"></div>
        </section>

        <section class="dash-card dash-card--radio" aria-labelledby="dash-radio-title" data-perm="radio">
          <header class="dash-card__head">
            <h2 id="dash-radio-title" class="dash-card__title">Radio</h2>
          </header>
          <div class="dash-radio" data-role="radio"></div>
        </section>
      </div>

      <!-- ROW 4 (4-up): Messages | Today's Schedule | Recent Activity | To-Do (per spec) -->
      <div class="dash-grid dash-grid--4">
        <section class="dash-card" aria-labelledby="dash-msg-title" data-perm="messages">
          <header class="dash-card__head">
            <h2 id="dash-msg-title" class="dash-card__title">Messages</h2>
            <button type="button" class="dash-card__link" data-role="msg-all">View all</button>
          </header>
          <ul class="dash-msg-list" data-role="messages"></ul>
        </section>

        <section class="dash-card" aria-labelledby="dash-sched-title" data-perm="calendar">
          <header class="dash-card__head">
            <h2 id="dash-sched-title" class="dash-card__title">Today's Schedule</h2>
            <button type="button" class="dash-card__link" data-role="sched-all">Full calendar</button>
          </header>
          <ul class="dash-sched-list" data-role="schedule"></ul>
        </section>

        <section class="dash-card" aria-labelledby="dash-activity-title" data-perm="crm">
          <header class="dash-card__head">
            <h2 id="dash-activity-title" class="dash-card__title">Recent Activity</h2>
            <button type="button" class="dash-card__link" data-role="activity-all">View all</button>
          </header>
          <ul class="dash-activity" data-role="activity"></ul>
        </section>

        <section class="dash-card" aria-labelledby="dash-todo-title">
          <header class="dash-card__head">
            <h2 id="dash-todo-title" class="dash-card__title">To-Do List</h2>
            <div class="dash-todo-scope" role="tablist" aria-label="To-do scope">
              <button type="button" role="tab" class="dash-todo-scope__btn is-active"
                data-role="todo-scope" data-scope="team" aria-selected="true">Team</button>
              <button type="button" role="tab" class="dash-todo-scope__btn"
                data-role="todo-scope" data-scope="personal" aria-selected="false">Personal</button>
            </div>
          </header>
          <form class="dash-todo-add" data-role="todo-form">
            <input type="text" data-role="todo-input" placeholder="Add a task…" aria-label="Add a task" />
            <button type="submit" class="dash-btn dash-btn--primary">Add</button>
          </form>
          <p class="dash-todo-hint" data-role="todo-hint">This task will be visible to your whole team.</p>
          <ul class="dash-todo-list" data-role="todos"></ul>
        </section>
      </div>

      <!-- ROW 5 (calendar wider 2fr | quick notes 1fr | needs attention 1fr) -->
      <div class="dash-grid dash-grid--cal-row">
        <section class="dash-card dash-card--feature" aria-labelledby="dash-cal-title" data-perm="calendar">
          <header class="dash-card__head">
            <h2 id="dash-cal-title" class="dash-card__title">Calendar</h2>
            <div class="dash-card__actions">
              <button type="button" class="dash-card__link" data-role="cal-add" title="Add event on this date">+ Add event</button>
              <button type="button" class="dash-card__link" data-role="cal-fullcal" title="Open the full calendar">Full calendar ›</button>
            </div>
          </header>
          <div class="dash-cal-toolbar">
            <button type="button" class="dash-cal-nav__btn" data-role="cal-prev" aria-label="Previous month">‹</button>
            <span class="dash-cal-nav__label" data-role="cal-month-label">—</span>
            <button type="button" class="dash-cal-nav__btn" data-role="cal-next" aria-label="Next month">›</button>
          </div>
          <div class="dash-cal-grid" data-role="cal-grid"></div>
          <section class="dash-cal-daylist" data-role="cal-daylist">
            <h3 class="dash-cal-daylist__title" data-role="cal-daylist-title">Today</h3>
            <ul class="dash-cal-daylist__items" data-role="cal-daylist-items"></ul>
          </section>
        </section>

        <section class="dash-card dash-card--feature" aria-labelledby="dash-notes-title">
          <header class="dash-card__head">
            <h2 id="dash-notes-title" class="dash-card__title">Quick Notes</h2>
            <button type="button" class="dash-card__link" data-role="notes-add">+ Add note</button>
          </header>
          <ul class="dash-notes-list" data-role="notes-list"></ul>
        </section>

        <section class="dash-card dash-card--feature" aria-labelledby="dash-attn-title" data-perm="operations">
          <header class="dash-card__head">
            <h2 id="dash-attn-title" class="dash-card__title">Needs Attention</h2>
            <button type="button" class="dash-card__link" data-role="snag-add">+ Add snag</button>
          </header>
          <ul class="dash-attn-list" data-role="attention"></ul>
        </section>
      </div>

      <!-- ROW 6 (3-up): Live Courier Tracking | Supplier Offers | Supplier News -->
      <div class="dash-grid dash-grid--3">
        <section class="dash-card dash-card--soon" id="w-courier-tracking" aria-labelledby="dash-cv-title" data-perm="delivery">
          <header class="dash-card__head">
            <h2 id="dash-cv-title" class="dash-card__title">
              <span class="dash-card__title-ico" aria-hidden="true">${ICO.pin}</span>
              Live Courier Tracking
            </h2>
            <button type="button" class="dash-card__link" data-role="cv-add">+ Add tracking</button>
          </header>
          <div class="dash-cv-list" data-role="couriers"></div>
          ${COMING_SOON}
        </section>

        <section class="dash-card dash-card--feed dash-card--soon" id="w-supplier-offers" aria-labelledby="dash-offers-title" data-perm="supplier-pricing">
          <header class="dash-card__head">
            <h2 id="dash-offers-title" class="dash-card__title">Live Supplier Offers</h2>
            <button type="button" class="dash-card__link" data-role="offers-refresh" title="Refresh">Refresh</button>
          </header>
          <div class="kos-sof-bar">
            <div class="kos-soft-seg" role="tablist" aria-label="Offers scope">
              <button class="active" type="button" role="tab" data-scope="mine"
                aria-selected="true">My Suppliers</button>
              <button type="button" role="tab" data-scope="all"
                aria-selected="false">Browse all offers</button>
            </div>
          </div>
          <div class="dash-offer-list dash-scroll-list" data-role="offers"></div>
          ${COMING_SOON}
        </section>

        <!-- Supplier News: connected-suppliers only — no scope toggle.
             Default audience match logic in renderNews uses scope='mine'. -->
        <section class="dash-card dash-card--feed dash-card--soon" id="w-supplier-news" aria-labelledby="dash-news-title" data-perm="supplier-pricing">
          <header class="dash-card__head">
            <h2 id="dash-news-title" class="dash-card__title">Supplier News</h2>
            <button type="button" class="dash-card__link" data-role="news-refresh" title="Refresh">Refresh</button>
          </header>
          <div class="dash-news-list dash-scroll-list" data-role="news"></div>
          ${COMING_SOON}
        </section>
      </div>

      <!-- ROW 7 (4-up): Google Reviews | My Links | Standard Links | News -->
      <div class="dash-grid dash-grid--4">
        <section class="widget" id="w-google-reviews" aria-labelledby="dash-rev-title">
          <div class="widget-head">
            <div class="widget-title" id="dash-rev-title" style="color:#fbbc04">
              <span class="ico"><svg viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285f4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34a853"/><path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z" fill="#fbbc04"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83c.87-2.6 3.3-4.52 6.16-4.52z" fill="#ea4335"/></svg></span>
              Google Reviews
            </div>
            <a class="link" href="https://www.google.com/search?q=Kitchen+OS+reviews" target="_blank" rel="noopener">Open <span>›</span></a>
          </div>
          <div class="widget-body" data-role="reviews"></div>
        </section>

        <section class="dash-card" aria-labelledby="dash-mylinks-title">
          <header class="dash-card__head">
            <h2 id="dash-mylinks-title" class="dash-card__title">My Links</h2>
            <button type="button" class="dash-card__link" data-role="add-link">+ Add Link</button>
          </header>
          <ul class="dash-links" data-role="my-links"></ul>
        </section>

        <section class="dash-card" aria-labelledby="dash-stdlinks-title">
          <header class="dash-card__head">
            <h2 id="dash-stdlinks-title" class="dash-card__title">Standard Links</h2>
            <button type="button" class="dash-card__link" data-role="manage-links">Manage</button>
          </header>
          <div class="dash-std" data-role="std-links"></div>
        </section>

        <section class="dash-card" aria-labelledby="dash-news-title">
          <header class="dash-card__head">
            <h2 id="dash-news-title" class="dash-card__title">News</h2>
            <span class="dash-pill dash-pill--ghost">Platform</span>
          </header>
          <ul class="dash-news-list" data-role="news"></ul>
        </section>
      </div>
    </section>
  `;
}

// Map stat-card keys to their permission gate. Role-gate hides the tile
// when the active role doesn't have the perm — and the 2-col grid
// reflows naturally around what's left.
const STAT_PERM_BY_KEY: Record<string, string> = {
  submissions: "submissions",
  messages:    "messages",
  deliveries:  "delivery",
  events:      "calendar",
};

function renderStats(host: HTMLElement, view: DashView, ctx: PageContext): void {
  host.innerHTML = view.cards
    .map(
      (c) => {
        const perm = STAT_PERM_BY_KEY[c.key];
        const permAttr = perm ? ` data-perm="${perm}"` : "";
        return `
          <article class="dash-stat" role="listitem" data-key="${escapeHtml(c.key)}"${permAttr}
            style="--dash-stat-color:${c.spark.color}">
            <div class="dash-stat__top">
              <span class="dash-stat__icon" aria-hidden="true">${iconMarkup(c.icon, { size: "sm" })}</span>
              <span class="dash-stat__label">${escapeHtml(c.label)}</span>
            </div>
            <div class="dash-stat__value">${c.value}</div>
            <div class="dash-spark">${sparklineSvg(c.spark)}</div>
          </article>
        `;
      },
    )
    .join("");

  for (const el of host.querySelectorAll<HTMLElement>(".dash-stat")) {
    const key = el.dataset["key"];
    const card = view.cards.find((c) => c.key === key);
    if (!card) continue;
    if (card.navTo) {
      el.classList.add("is-clickable");
      el.addEventListener("click", () => {
        if (card.navTo) ctx.navigate(card.navTo);
      });
    }
  }
}

interface QuickAction {
  label: string;
  icon: IconName;
  tone: "blue" | "orange" | "green" | "purple";
  go: () => void;
  /** Permission key from the 18-perm catalogue. Role-gate hides the tile
   *  for roles that lack this perm. Omit for actions every role can use. */
  perm?: string;
}

function buildQuickActions(ctx: PageContext): QuickAction[] {
  return [
    { label: "New Customer", icon: "customer", tone: "blue", perm: "crm",
      go: () => {
        // Open the modal in place — do NOT navigate to CRM, or the popup
        // ends up layered over the wrong page. On submit the modal closes
        // and broadcasts tkos:customer-created, so an open CRM refreshes.
        openNewCustomerModal({ showToast: ctx.showToast });
      } },
    { label: "New Quote",    icon: "file",     tone: "orange", perm: "crm",        go: () => ctx.navigate("quote-builder", { fresh: "1" }) },
    { label: "Messages",     icon: "chat",     tone: "blue",   perm: "messages",   go: () => ctx.navigate("messages") },
    { label: "CRM",          icon: "chart",    tone: "purple", perm: "crm",        go: () => ctx.navigate("crm") },
    { label: "Calendar",     icon: "calendar", tone: "green",  perm: "calendar",   go: () => ctx.navigate("calendar") },
    { label: "Suppliers",    icon: "package",  tone: "green",  perm: "suppliers",  go: () => ctx.navigate("suppliers") },
    // Social AI removed from nav for V1 (returns in V1.5 when properly built).
    // The AI render tool was removed entirely (page + modal) on 2026-06-21.
    { label: "Settings",     icon: "gear",     tone: "purple", perm: "settings",   go: () => ctx.navigate("settings") },
  ];
}

function renderQuickActions(host: HTMLElement, ctx: PageContext): void {
  const actions = buildQuickActions(ctx);
  host.innerHTML = actions
    .map(
      (a, i) => {
        const permAttr = a.perm ? ` data-perm="${a.perm}"` : "";
        return `
          <button class="dash-qa__tile dash-qa__tile--${a.tone}" type="button" data-i="${i}"${permAttr}>
            <span class="dash-qa__ico" aria-hidden="true">${iconMarkup(a.icon, { size: "lg" })}</span>
            <span class="dash-qa__lbl">${escapeHtml(a.label)}</span>
          </button>
        `;
      },
    )
    .join("");

  for (const btn of host.querySelectorAll<HTMLButtonElement>("button[data-i]")) {
    const idx = Number(btn.dataset["i"]);
    const action = actions[idx];
    if (!action) continue;
    btn.addEventListener("click", () => {
      action.go();
    });
  }
}

// Colour-coded pipeline stages. One canonical colour per stage drives the
// segmented bar, the per-stage dot, and the count tint.
const PIPE_STAGES: { key: PipelineStage; label: string; color: string }[] = [
  { key: "lead",      label: "Lead",      color: "#3b82f6" },
  { key: "qualified", label: "Qualified", color: "#a855f7" },
  { key: "quoted",    label: "Quoted",    color: "#f59e0b" },
  { key: "won",       label: "Won",       color: "#22c55e" },
  { key: "lost",      label: "Lost",      color: "#94a3b8" },
];

function renderPipeline(host: HTMLElement, p: PipelineCounts, ctx: PageContext): void {
  // Hero = open opportunities still in play (lead → qualified → quoted).
  const open = p.lead + p.qualified + p.quoted;
  const totalAll = open + p.won + p.lost;
  const segMax = totalAll || 1;

  const segments = PIPE_STAGES
    .map((s) => {
      const pct = (p[s.key] / segMax) * 100;
      if (pct <= 0) return "";
      return `<span class="dash-pipe__seg" style="width:${pct.toFixed(2)}%;background:${s.color}" title="${escapeHtml(s.label)}: ${p[s.key]}"></span>`;
    })
    .join("");

  const cells = PIPE_STAGES
    .map(
      (s) => `
        <button class="dash-pipe__cell" type="button" data-stage="${s.key}" style="--stage:${s.color}"
          aria-label="${escapeHtml(s.label)}: ${p[s.key]}">
          <span class="dash-pipe__dot" aria-hidden="true"></span>
          <span class="dash-pipe__count">${p[s.key]}</span>
          <span class="dash-pipe__label">${escapeHtml(s.label)}</span>
        </button>
      `,
    )
    .join("");

  host.innerHTML = `
    <div class="dash-pipe__hero">
      <span class="dash-pipe__hero-num">${open}</span>
      <span class="dash-pipe__hero-meta">
        <span class="dash-pipe__hero-lbl">Open leads</span>
        <span class="dash-pipe__hero-sub">${p.won} won · ${p.lost} lost</span>
      </span>
    </div>
    <div class="dash-pipe__bar" aria-hidden="true">${segments || `<span class="dash-pipe__seg dash-pipe__seg--empty"></span>`}</div>
    <div class="dash-pipe__grid">${cells}</div>
  `;

  for (const btn of host.querySelectorAll<HTMLButtonElement>("button[data-stage]")) {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      ctx.navigate("crm");
    });
  }
}

/**
 * Subtle area+line trend sparkline for the Revenue Overview hero. Stretches to
 * the host width (preserveAspectRatio=none) with a non-scaling stroke so the
 * line stays crisp at any width. Returns "" for an empty trail so the empty
 * state collapses cleanly.
 */
function revSparkline(values: number[]): string {
  if (values.length === 0) return "";
  const W = 100;
  const H = 30;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const stepX = values.length > 1 ? W / (values.length - 1) : W;
  const pts = values.map((v, i) => {
    const x = values.length > 1 ? i * stepX : W / 2;
    const y = H - 3 - ((v - min) / span) * (H - 6);
    return [x, y] as const;
  });
  const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const area = `${line} L${W} ${H} L0 ${H} Z`;
  return `
    <svg class="dash-rev__spark" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id="dashRevSparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="var(--color-amber)" stop-opacity="0.26"/>
          <stop offset="100%" stop-color="var(--color-amber)" stop-opacity="0"/>
        </linearGradient>
      </defs>
      <path d="${area}" fill="url(#dashRevSparkFill)"/>
      <path d="${line}" fill="none" stroke="var(--color-amber)" stroke-width="1.5"
        stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>
    </svg>
  `;
}

const REV_ARROW_UP = `<svg viewBox="0 0 16 16" class="dash-rev__delta-ico" aria-hidden="true"><path d="M8 12.5V4M8 4L4.5 7.5M8 4L11.5 7.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const REV_ARROW_DOWN = `<svg viewBox="0 0 16 16" class="dash-rev__delta-ico" aria-hidden="true"><path d="M8 3.5V12M8 12L4.5 8.5M8 12L11.5 8.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

// Count-up: animate a number element 0 → target once on first paint. The real
// value is already in the DOM (template), so this only replays as a flourish;
// reduced-motion / repeat paints just show the final figure.
let revCountUpDone = false;
function countUpEl(el: HTMLElement, target: number, fmt: (n: number) => string, duration = 1200): void {
  if (!(target > 0) || (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches)) {
    el.textContent = fmt(target); return;
  }
  const start = performance.now();
  const step = (t: number): void => {
    const p = Math.min((t - start) / duration, 1);
    const eased = 1 - Math.pow(1 - p, 3);
    el.textContent = fmt(Math.round(target * eased));
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function renderRevenue(host: HTMLElement, r: RevenueBlock): void {
  const deltaPct = r.lastMonthGbp === 0
    ? 0
    : ((r.thisMonthGbp - r.lastMonthGbp) / r.lastMonthGbp) * 100;
  const up = deltaPct >= 0;
  const deltaClass = deltaPct === 0
    ? "dash-rev__delta-pill--flat"
    : up ? "dash-rev__delta-pill--up" : "dash-rev__delta-pill--down";
  const deltaIco = deltaPct === 0 ? "" : up ? REV_ARROW_UP : REV_ARROW_DOWN;
  const deltaSign = up ? "+" : "";
  const hasTarget = r.targetGbp > 0;
  const targetPct = hasTarget ? Math.min(100, (r.ytdGbp / r.targetGbp) * 100) : 0;
  const targetSub = hasTarget
    ? `Target ${GBP_0.format(r.targetGbp)} · ${targetPct.toFixed(0)}%`
    : "No target set";
  const spark = revSparkline(r.trail.map((t) => t.value));

  host.innerHTML = `
    <div class="dash-rev__hero">
      <div class="dash-rev__hero-l">
        <div class="dash-rev__lbl">This month</div>
        <div class="dash-rev__big">${GBP_0.format(r.thisMonthGbp)}</div>
      </div>
      <div class="dash-rev__delta-pill ${deltaClass}">${deltaIco}<span>${deltaSign}${deltaPct.toFixed(1)}%</span></div>
    </div>
    <div class="dash-rev__caption">vs last month</div>
    ${spark ? `<div class="dash-rev__spark-wrap">${spark}</div>` : ""}
    <div class="dash-rev__ytd">
      <div class="dash-rev__ytd-head">
        <span class="dash-rev__lbl">Year to date</span>
        <span class="dash-rev__med">${GBP_0.format(r.ytdGbp)}</span>
      </div>
      <div class="dash-rev__bar-track"><div class="dash-rev__bar-fill" style="width:${targetPct.toFixed(2)}%"></div></div>
      <div class="dash-rev__lbl-sub">${targetSub}</div>
    </div>
  `;

  // One orchestrated moment: count the headline figures up on first load.
  if (!revCountUpDone) {
    revCountUpDone = true;
    const big = host.querySelector<HTMLElement>(".dash-rev__big");
    const med = host.querySelector<HTMLElement>(".dash-rev__med");
    if (big) countUpEl(big, r.thisMonthGbp, (n) => GBP_0.format(n));
    if (med) countUpEl(med, r.ytdGbp, (n) => GBP_0.format(n));
  }
}

function renderRadio(host: HTMLElement, ctx: PageContext): PageCleanup {
  const presets = getRadioPresets();
  const initial = getRadioState();

  host.innerHTML = `
    <div class="dash-radio__now">
      <button type="button" class="dash-radio__play" data-role="radio-play" aria-label="Play / Pause"></button>
      <div class="dash-radio__meta">
        <div class="dash-radio__title" data-role="radio-station">${escapeHtml(initial.current?.name ?? "Pick a station")}</div>
        <div class="dash-radio__sub" data-role="radio-sub">Tap a preset below or search</div>
      </div>
      <div class="dash-radio__vol">
        <input type="range" min="0" max="100" value="${initial.volume}" data-role="radio-vol" aria-label="Volume" />
      </div>
    </div>
    <div class="dash-radio__search">
      <span class="dash-radio__search-icon" aria-hidden="true">${ICO.search}</span>
      <input type="search" placeholder="Search stations (e.g. jazz, BBC, country…)" data-role="radio-search" aria-label="Search stations" />
    </div>
    <div class="dash-radio__results" data-role="radio-results" hidden></div>
    <div class="dash-radio__presets" data-role="radio-presets">
      ${presets
        .map(
          (p) => `
        <button type="button" class="dash-radio__preset" data-pid="${escapeHtml(p.id)}" aria-pressed="false">
          <span class="dash-radio__logo" style="background:${escapeHtml(p.bg)}">${escapeHtml(p.logo)}</span>
          <span class="dash-radio__preset-meta">
            <span class="dash-radio__preset-name">${escapeHtml(p.name)}</span>
            <span class="dash-radio__preset-genre">${escapeHtml(p.genre)}</span>
          </span>
        </button>`,
        )
        .join("")}
    </div>
  `;

  const playBtn = host.querySelector<HTMLButtonElement>("[data-role='radio-play']");
  const titleEl = host.querySelector<HTMLElement>("[data-role='radio-station']");
  const subEl = host.querySelector<HTMLElement>("[data-role='radio-sub']");
  const volEl = host.querySelector<HTMLInputElement>("[data-role='radio-vol']");
  const searchInput = host.querySelector<HTMLInputElement>("[data-role='radio-search']");
  const resultsBox = host.querySelector<HTMLElement>("[data-role='radio-results']");

  function paint(state: RadioState): void {
    if (titleEl) titleEl.textContent = state.current?.name ?? "Pick a station";
    if (subEl) {
      if (state.status === "loading") subEl.textContent = "Connecting…";
      else if (state.status === "playing") subEl.textContent = "Now playing · Live UK radio";
      else if (state.status === "paused") subEl.textContent = "Paused";
      else if (state.status === "error") subEl.textContent = "Stream error — try another";
      else subEl.textContent = state.current ? "Tap play to start" : "Tap a preset below or search";
    }
    const playing = state.status === "playing" || state.status === "loading";
    if (playBtn) {
      playBtn.innerHTML = playing
        ? '<svg viewBox="0 0 24 24"><rect x="6" y="4.5" width="4" height="15" rx="0.8"/><rect x="14" y="4.5" width="4" height="15" rx="0.8"/></svg>'
        : '<svg viewBox="0 0 24 24"><polygon points="6 4 20 12 6 20"/></svg>';
      playBtn.setAttribute("aria-label", playing ? "Pause" : "Play");
    }
    host.classList.toggle("is-playing", state.status === "playing");
    host.classList.toggle("is-loading", state.status === "loading");
    if (volEl && document.activeElement !== volEl) volEl.value = String(state.volume);
    for (const btn of host.querySelectorAll<HTMLButtonElement>("[data-pid]")) {
      const active = btn.dataset["pid"] === state.presetId;
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
    }
  }

  const unsub = subscribeRadio(paint);

  playBtn?.addEventListener("click", () => toggleRadio());

  volEl?.addEventListener("input", () => {
    setVolume(Number(volEl.value || "0"));
  });

  for (const btn of host.querySelectorAll<HTMLButtonElement>("[data-pid]")) {
    btn.addEventListener("click", () => {
      const id = btn.dataset["pid"];
      if (id) selectPreset(id, true);
    });
  }

  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  let searchSeq = 0;
  function paintResults(items: SearchHit[]): void {
    if (!resultsBox) return;
    if (items.length === 0) {
      resultsBox.hidden = true;
      resultsBox.innerHTML = "";
      return;
    }
    resultsBox.hidden = false;
    resultsBox.innerHTML = items
      .map(
        (s, i) => `
        <button type="button" class="dash-radio__result" data-i="${i}">
          <span class="dash-radio__rdot"></span>
          <span class="dash-radio__rname">${escapeHtml(s.name)}</span>
          ${s.country ? `<span class="dash-radio__rcc">${escapeHtml(s.country)}</span>` : ""}
        </button>`,
      )
      .join("");
    for (const btn of resultsBox.querySelectorAll<HTMLButtonElement>("[data-i]")) {
      btn.addEventListener("click", () => {
        const idx = Number(btn.dataset["i"]);
        const hit = items[idx];
        if (!hit) return;
        selectStation({ name: hit.name, streams: [hit.url], ...(hit.country ? { country: hit.country } : {}) }, true);
      });
    }
  }

  searchInput?.addEventListener("input", () => {
    const q = searchInput.value.trim();
    if (searchTimer) clearTimeout(searchTimer);
    if (!q) {
      paintResults([]);
      return;
    }
    const mySeq = ++searchSeq;
    searchTimer = setTimeout(() => {
      void searchStations(q).then((items) => {
        if (mySeq !== searchSeq) return;
        paintResults(items);
      }).catch(() => {
        if (mySeq !== searchSeq) return;
        ctx.showToast("Radio search failed — try again", "error");
      });
    }, 280);
  });

  return () => {
    unsub();
    if (searchTimer) clearTimeout(searchTimer);
  };
}

function renderSchedule(host: HTMLElement, items: ScheduleItem[], ctx: PageContext): void {
  if (items.length === 0) {
    host.innerHTML = `
      <li class="dash-sched-empty">
        <span class="dash-sched-empty__ico" aria-hidden="true">${iconMarkup("calendar", { size: "lg" })}</span>
        <span class="dash-sched-empty__title">Nothing scheduled today</span>
        <button type="button" class="dash-sched-empty__add" data-role="sched-add">+ Add event</button>
      </li>`;
    host
      .querySelector<HTMLButtonElement>("[data-role='sched-add']")
      ?.addEventListener("click", () => ctx.navigate("calendar"));
    return;
  }
  // Action icons — inline SVG (1.5px stroke, rounded caps, 24×24 — house style).
  const SVG = (inner: string): string =>
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;
  const ICON_ACCEPT = SVG(`<path d="M5 13l4 4L19 7"/>`);
  const ICON_DECLINE = SVG(`<path d="M6 6l12 12M18 6L6 18"/>`);
  const ICON_DISMISS = SVG(`<path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2M6 8l1 12a1 1 0 001 1h8a1 1 0 001-1l1-12"/>`);

  host.innerHTML = items
    .map(
      (s) => `
        <li class="dash-sched dash-sched--${s.tone}" data-id="${escapeHtml(s.id)}">
          <span class="dash-sched__time">${escapeHtml(s.time)}</span>
          <div class="dash-sched__body">
            <div class="dash-sched__title">${escapeHtml(s.title)}</div>
            <div class="dash-sched__where">${escapeHtml(s.where)}</div>
          </div>
          <div class="dash-sched__acts">
            <button type="button" class="dash-sched__act dash-sched__act--accept" data-act="accept" title="Accept" aria-label="Accept appointment">${ICON_ACCEPT}</button>
            <button type="button" class="dash-sched__act dash-sched__act--decline" data-act="decline" title="Decline" aria-label="Decline appointment">${ICON_DECLINE}</button>
            <button type="button" class="dash-sched__act dash-sched__act--dismiss" data-act="dismiss" title="Dismiss" aria-label="Dismiss appointment">${ICON_DISMISS}</button>
          </div>
        </li>
      `,
    )
    .join("");

  const ACT_TOAST: Record<string, string> = {
    accept: "Appointment accepted",
    decline: "Appointment declined",
    dismiss: "Appointment dismissed",
  };

  for (const li of host.querySelectorAll<HTMLElement>(".dash-sched")) {
    li.addEventListener("click", () => {
      const id = li.dataset["id"] ?? "";
      const item = items.find((x) => x.id === id);
      if (item) openScheduleDetail(item, ctx);
    });
    for (const btn of li.querySelectorAll<HTMLButtonElement>(".dash-sched__act")) {
      btn.addEventListener("click", async (e) => {
        e.stopPropagation(); // don't open the detail popup
        const id = li.dataset["id"] ?? "";
        const act = btn.dataset["act"] ?? "";
        if (!id || !act) return;
        const path =
          act === "dismiss"
            ? `/portal/appointments/${encodeURIComponent(id)}`
            : `/portal/appointments/${encodeURIComponent(id)}/${act}`;
        const method = act === "dismiss" ? "DELETE" : "POST";
        const buttons = li.querySelectorAll<HTMLButtonElement>("button");
        buttons.forEach((b) => (b.disabled = true));
        try {
          await ctx.api(path, { method });
          li.remove(); // optimistic in-place removal — no full re-render
          ctx.showToast(ACT_TOAST[act] ?? "Done", "success");
          if (host.querySelectorAll(".dash-sched").length === 0) renderSchedule(host, [], ctx);
        } catch {
          buttons.forEach((b) => (b.disabled = false));
          ctx.showToast("Couldn't update the appointment — please try again.", "error");
        }
      });
    }
  }
}

// Click-through detail popup for a Today's-Schedule event. Shows the event's
// details; "Edit" + "Open Calendar" jump to the full calendar (Edit lands on
// the event's date — schedule rows are appointment previews whose ids aren't
// calendar-event ids, so we navigate rather than open the calendar-event editor
// on the wrong record).
function openScheduleDetail(item: ScheduleItem, ctx: PageContext): void {
  let whenDate = "";
  if (item.date) {
    const d = new Date(item.date);
    if (!Number.isNaN(d.getTime())) {
      whenDate = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
    }
  }
  const when = [whenDate, item.time && item.time !== "—" ? item.time : ""].filter(Boolean).join(" · ");
  const rows: string[] = [];
  if (when) rows.push(`<dt>When</dt><dd>${escapeHtml(when)}</dd>`);
  if (item.customer) rows.push(`<dt>Customer</dt><dd>${escapeHtml(item.customer)}</dd>`);
  if (item.type) rows.push(`<dt>Type</dt><dd>${escapeHtml(item.type)}</dd>`);
  if (item.where && item.where !== item.type) rows.push(`<dt>Detail</dt><dd>${escapeHtml(item.where)}</dd>`);
  if (item.notes) rows.push(`<dt>Notes</dt><dd>${escapeHtml(item.notes)}</dd>`);

  const modal = createModal({
    title: item.title || "Event",
    size: "sm",
    content: (body) => {
      body.innerHTML = `<dl class="dash-event-detail">${rows.join("") || "<dd>No further details.</dd>"}</dl>`;
    },
    actions: [
      { label: "Close", variant: "ghost" },
      { label: "Open Calendar", variant: "ghost", onClick: () => ctx.navigate("calendar") },
      { label: "Edit", variant: "primary", onClick: () => ctx.navigate("calendar", item.date ? { date: item.date } : undefined) },
    ],
  });
  modal.open();
}

// ── Calendar widget (V1: month grid + prev/next nav, no events, no clicks) ──

interface CalendarCursor {
  year: number;
  month: number; // 0–11
}

interface MiniCalEvent {
  /** Master row id when this isn't a recurrence expansion; for our
   *  dashboard widget the server only returns master rows so this is
   *  always the master id. */
  id: string;
  date: string; // YYYY-MM-DD
  type: string;
  title: string;
  time: string;
  endTime: string;
  allDay: boolean;
  notes: string;
  customer: string;
  recurrence: "none" | "daily" | "weekly" | "monthly" | "yearly";
}

const CAL_DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
const CAL_MONTH_FMT = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" });

// Verbatim colour map from apps/retailer/src/pages/calendar/page.ts TYPES.
// Kept inline rather than imported so the dashboard doesn't pull in the
// calendar page's offline-first machinery just to render dots.
const MINI_CAL_TYPE_COLOR: Readonly<Record<string, string>> = {
  enquiry: "#e58200",
  delivery: "#3b82f6",
  booking: "#22c55e",
  installation: "#a855f7",
  personal: "#8a8f99",
  followup: "#fbbf24",
  survey: "#14b8a6",
  snag: "#f59e0b",
  payment: "#ef4444",
  team: "#6366f1",
};

function miniCalColor(type: string): string {
  return MINI_CAL_TYPE_COLOR[type] ?? "var(--color-amber)";
}

function todayYMDLocal(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

/** Humanise a raw event-type slug for display, e.g. "home_visit" → "Home
 *  Visit", "design_consultation" → "Design Consultation". Already-pretty
 *  labels ("Appointment") pass through unchanged. */
function humaniseEventLabel(raw: string): string {
  const s = String(raw || "").trim();
  if (!s) return "";
  return s
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");
}

/** Wizard "/start/" links must point at the wizard host (dev.tkos.io), never
 *  the portal host. Older My-Links bookmarks captured `window.location.origin`
 *  (= portal.tkos.io) at the time they were saved, so they show the wrong
 *  domain. Rewrite the host on read so the displayed/clicked link works. */
function fixWizardLinkHost(url: string): string {
  try {
    const u = new URL(url);
    if (/\/start\//.test(u.pathname) && u.host !== "dev.tkos.io") {
      u.protocol = "https:";
      u.host = "dev.tkos.io";
      return u.toString();
    }
  } catch {
    /* not an absolute URL — leave as-is */
  }
  return url;
}

function renderCalendarWidget(
  labelEl: HTMLElement,
  gridEl: HTMLElement,
  cursor: CalendarCursor,
  eventsByDate: Map<string, MiniCalEvent[]>,
  selected: string,
  onSelect: (ymd: string) => void,
): void {
  const ref = new Date(cursor.year, cursor.month, 1);
  labelEl.textContent = CAL_MONTH_FMT.format(ref);

  const lastDay = new Date(cursor.year, cursor.month + 1, 0).getDate();
  const startOffset = (ref.getDay() + 6) % 7;
  const todayYMD = todayYMDLocal();

  let html = `<div class="dash-cal-dow-row">${CAL_DOW
    .map((d) => `<div class="dash-cal-dow">${d}</div>`)
    .join("")}</div><div class="dash-cal-days">`;

  for (let i = 0; i < startOffset; i++) {
    html += `<div class="dash-cal-cell dash-cal-cell--muted"></div>`;
  }
  for (let d = 1; d <= lastDay; d++) {
    const ymd = `${cursor.year}-${pad2(cursor.month + 1)}-${pad2(d)}`;
    const dow = (startOffset + d - 1) % 7;
    const isWeekend = dow >= 5;
    const isToday = ymd === todayYMD;
    const isSelected = ymd === selected;
    const dayEvents = eventsByDate.get(ymd) ?? [];

    const dotsHtml =
      dayEvents.length > 0
        ? `<span class="dash-cal-cell__dots" aria-label="${dayEvents.length} events">${dayEvents
            .slice(0, 3)
            .map(
              (e) =>
                `<span class="dash-cal-cell__dot" style="background:${escapeHtml(
                  miniCalColor(e.type),
                )}" title="${escapeHtml(e.title)}"></span>`,
            )
            .join("")}${dayEvents.length > 3 ? `<span class="dash-cal-cell__dot-more">+${dayEvents.length - 3}</span>` : ""}</span>`
        : "";

    const cls =
      `dash-cal-cell` +
      (isWeekend ? " dash-cal-cell--weekend" : "") +
      (isToday ? " dash-cal-cell--today" : "") +
      (isSelected ? " dash-cal-cell--selected" : "") +
      (dayEvents.length > 0 ? " dash-cal-cell--has-events" : "");
    html += `<button type="button" class="${cls}" data-ymd="${ymd}" aria-label="${ymd}${dayEvents.length > 0 ? `, ${dayEvents.length} events` : ""}"><span class="dash-cal-cell__num">${d}</span>${dotsHtml}</button>`;
  }
  html += `</div>`;
  gridEl.innerHTML = html;

  // Click a day → update the dashboard day-list (NOT navigate). The
  // user explicitly hits "Full calendar ›" or "+ Add event" to jump
  // to the calendar page.
  for (const cell of gridEl.querySelectorAll<HTMLButtonElement>("button.dash-cal-cell[data-ymd]")) {
    cell.addEventListener("click", () => {
      const ymd = cell.dataset["ymd"];
      if (ymd) onSelect(ymd);
    });
  }
}

interface ServerCalEvent {
  id?: string;
  title?: string;
  type?: string;
  date?: string;
  time?: string;
  endTime?: string;
  allDay?: boolean;
  notes?: string;
  customer?: string;
  recurrence?: string;
}

function coerceMiniRec(v: unknown): MiniCalEvent["recurrence"] {
  const s = String(v ?? "none").toLowerCase();
  if (s === "daily" || s === "weekly" || s === "monthly" || s === "yearly") return s;
  return "none";
}

function expandIntoMonth(
  master: MiniCalEvent,
  cursor: CalendarCursor,
  out: Map<string, MiniCalEvent[]>,
): void {
  const monthStart = new Date(cursor.year, cursor.month, 1);
  const monthEnd = new Date(cursor.year, cursor.month + 1, 0);
  const start = new Date(master.date + "T00:00:00");
  if (!Number.isFinite(start.getTime())) return;

  // Skip recurrences that haven't started yet.
  if (start > monthEnd) {
    if (master.recurrence === "none") return;
  }

  const push = (ymd: string): void => {
    if (ymd < `${cursor.year}-${pad2(cursor.month + 1)}-01`) return;
    if (ymd > `${cursor.year}-${pad2(cursor.month + 1)}-${pad2(monthEnd.getDate())}`) return;
    const list = out.get(ymd) ?? [];
    list.push({ ...master, date: ymd });
    out.set(ymd, list);
  };

  if (master.recurrence === "none") {
    push(master.date);
    return;
  }

  // Walk from `start` forward until past monthEnd, but cap to avoid runaway.
  const cursor2 = new Date(start);
  let safety = 5000;
  while (cursor2.getTime() <= monthEnd.getTime() && safety-- > 0) {
    if (cursor2.getTime() >= monthStart.getTime()) {
      push(
        `${cursor2.getFullYear()}-${pad2(cursor2.getMonth() + 1)}-${pad2(cursor2.getDate())}`,
      );
    }
    switch (master.recurrence) {
      case "daily":   cursor2.setDate(cursor2.getDate() + 1); break;
      case "weekly":  cursor2.setDate(cursor2.getDate() + 7); break;
      case "monthly": cursor2.setMonth(cursor2.getMonth() + 1); break;
      case "yearly":  cursor2.setFullYear(cursor2.getFullYear() + 1); break;
      default: safety = 0; break;
    }
  }
}

// Convert an arbitrary "when" value (ms or ISO string) to a YMD + time pair
// in local time. Mirrors calendar/page.ts deriveFromRow so the dashboard
// widget and the full calendar agree on date placement.
function whenToYmdTime(when: unknown): { date: string; time: string; allDay: boolean } | null {
  let ms: number | null = null;
  if (typeof when === "number" && Number.isFinite(when)) {
    ms = when;
  } else if (typeof when === "string" && when.trim()) {
    const parsed = Date.parse(when);
    if (Number.isFinite(parsed)) ms = parsed;
  }
  if (ms === null) return null;
  const d = new Date(ms);
  const date = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const allDay = d.getHours() === 0 && d.getMinutes() === 0;
  const time = allDay ? "" : `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return { date, time, allDay };
}

async function fetchMonthEvents(cursor: CalendarCursor): Promise<Map<string, MiniCalEvent[]>> {
  const map = new Map<string, MiniCalEvent[]>();
  const seen = new Set<string>(); // dedupe by id across sources.

  const pushMaster = (m: MiniCalEvent): void => {
    if (seen.has(m.id)) return;
    seen.add(m.id);
    expandIntoMonth(m, cursor, map);
  };

  // 1. Master /portal/calendar events (user-created via Add Event in the
  //    Calendar page or this widget). Recurrence is supported here.
  try {
    const res = await api<{ ok: boolean; events: ServerCalEvent[] }>(`/portal/calendar`);
    for (const raw of res.events ?? []) {
      if (typeof raw.date !== "string") continue;
      const master: MiniCalEvent = {
        id: String(raw.id ?? ""),
        date: raw.date,
        type: String(raw.type ?? "booking"),
        title: String(raw.title ?? ""),
        time: String(raw.time ?? ""),
        endTime: String(raw.endTime ?? ""),
        allDay: Boolean(raw.allDay),
        notes: String(raw.notes ?? ""),
        customer: String(raw.customer ?? ""),
        recurrence: coerceMiniRec(raw.recurrence),
      };
      pushMaster(master);
    }
  } catch (err) {
    console.error("[mini-calendar] /portal/calendar fetch failed", err);
  }

  // 2. Derived from /portal/bookings — bookings created in the Bookings page
  //    DON'T post to /portal/calendar, so without this they were invisible
  //    on the dashboard widget while still appearing on the full calendar
  //    page (which also derives from /portal/bookings). Mirror those
  //    derivations so the two surfaces stay consistent. Read-only, no
  //    recurrence.
  try {
    interface BkRow {
      id?: string; customer?: string; type?: string;
      when?: unknown; date?: unknown; scheduledAt?: unknown;
      notes?: string; calendarEventId?: string | null;
    }
    const resp = await api<{ bookings?: BkRow[] }>("/portal/bookings");
    for (const row of resp?.bookings ?? []) {
      // Already on the calendar as its own event — see the same guard in
      // pages/calendar/page.ts. Deriving it again double-books the day.
      if (row.calendarEventId) continue;
      const yt = whenToYmdTime(row.when ?? row.date ?? row.scheduledAt);
      if (!yt) continue;
      pushMaster({
        id: `bk-${String(row.id ?? "")}`,
        date: yt.date,
        type: String(row.type ?? "booking"),
        title: `Booking · ${String(row.customer ?? "Customer")}`,
        time: yt.time,
        endTime: "",
        allDay: yt.allDay,
        notes: typeof row.notes === "string" ? row.notes : "",
        customer: typeof row.customer === "string" ? row.customer : "",
        recurrence: "none",
      });
    }
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 404) {
      console.warn("[mini-calendar] /portal/bookings derive failed", err);
    }
  }

  // 3. Derived from /portal/deliveries — same logic; deliveries are scheduled
  //    via the Deliveries page, not the calendar.
  try {
    interface DlRow {
      id?: string; customer?: string; ref?: string;
      eta?: unknown; date?: unknown; scheduledAt?: unknown;
    }
    const resp = await api<{ deliveries?: DlRow[] }>("/portal/deliveries");
    for (const row of resp?.deliveries ?? []) {
      const yt = whenToYmdTime(row.eta ?? row.date ?? row.scheduledAt);
      if (!yt) continue;
      pushMaster({
        id: `dl-${String(row.id ?? "")}`,
        date: yt.date,
        type: "delivery",
        title: `Delivery · ${String(row.customer ?? row.ref ?? "Customer")}`,
        time: yt.time,
        endTime: "",
        allDay: yt.allDay,
        notes: "",
        customer: typeof row.customer === "string" ? row.customer : "",
        recurrence: "none",
      });
    }
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 404) {
      console.warn("[mini-calendar] /portal/deliveries derive failed", err);
    }
  }

  return map;
}

function stepCursor(cursor: CalendarCursor, dir: -1 | 1): CalendarCursor {
  let m = cursor.month + dir;
  let y = cursor.year;
  if (m < 0) {
    m = 11;
    y -= 1;
  } else if (m > 11) {
    m = 0;
    y += 1;
  }
  return { year: y, month: m };
}

// Recent Activity is now a self-contained, PG-backed widget (mountActivityWidget)
// wired in the mount block — not driven by renderAll/demo data.

// ── Needs Attention (snag list) ─────────────────────────────────────────────
// PG-backed (migration 0013 + /api/portal/snags). Each snag carries a
// customer name, title and content. Click a row → detail popup with full
// content + a "Mark resolved" button (resolve stamps resolved_at and the
// snag drops off the open list). + Add snag opens an inline create form.

type SnagPriority = "high" | "medium" | "low";

interface SnagRow {
  id: string;
  customerName: string;
  title: string;
  content: string;
  priority: SnagPriority;
  resolvedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

interface SnagHandle {
  cleanup: () => void;
}

interface SnagMountOpts {
  listEl: HTMLElement;
  addBtn: HTMLButtonElement;
  ctx: PageContext;
}

function mountSnagList(opts: SnagMountOpts): SnagHandle {
  const { listEl, addBtn, ctx } = opts;
  let items: SnagRow[] = [];
  let destroyed = false;
  let popupCleanup: (() => void) | null = null;

  function rowMarkup(s: SnagRow): string {
    const cust = s.customerName ? `<span class="dash-attn__cust">${escapeHtml(s.customerName)}</span>` : "";
    return `
      <li class="dash-attn dash-attn--${escapeHtml(s.priority)}" data-id="${escapeHtml(s.id)}">
        <button type="button" class="dash-attn__btn" data-role="snag-open">
          <span class="dash-attn__main">
            ${cust}
            <span class="dash-attn__title">${escapeHtml(s.title)}</span>
          </span>
          <span class="dash-attn__pill dash-attn__pill--${escapeHtml(s.priority)}">${s.priority.toUpperCase()}</span>
        </button>
      </li>`;
  }

  function paint(): void {
    if (items.length === 0) {
      listEl.innerHTML = `<li class="dash-empty">All clear — nothing flagged.</li>`;
      return;
    }
    listEl.innerHTML = items.map(rowMarkup).join("");
    for (const btn of listEl.querySelectorAll<HTMLButtonElement>("[data-role='snag-open']")) {
      btn.addEventListener("click", () => {
        const id = btn.closest<HTMLElement>(".dash-attn")?.dataset["id"];
        const s = items.find((x) => x.id === id);
        if (s) openDetail(s);
      });
    }
  }

  async function load(): Promise<void> {
    try {
      const res = await api<{ ok: boolean; snags: SnagRow[] }>(`/portal/snags`);
      if (destroyed) return;
      items = Array.isArray(res.snags) ? res.snags : [];
      paint();
    } catch (err) {
      console.error("[snags] load failed", err);
      if (!destroyed) listEl.innerHTML = `<li class="dash-empty">Couldn't load snags — retrying…</li>`;
    }
  }

  function closePopup(): void {
    if (popupCleanup) {
      popupCleanup();
      popupCleanup = null;
    }
  }

  function buildOverlay(): { overlay: HTMLElement; panel: HTMLElement } {
    const overlay = document.createElement("div");
    overlay.className = "dash-snag-overlay";
    const panel = document.createElement("div");
    panel.className = "dash-snag-panel";
    overlay.appendChild(panel);

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") closePopup();
    };
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) closePopup();
    });
    document.addEventListener("keydown", onKey);
    document.body.appendChild(overlay);

    popupCleanup = () => {
      document.removeEventListener("keydown", onKey);
      overlay.remove();
    };
    return { overlay, panel };
  }

  function openDetail(s: SnagRow): void {
    closePopup();
    const { panel } = buildOverlay();
    panel.innerHTML = `
      <header class="dash-snag-panel__head">
        <span class="dash-attn__pill dash-attn__pill--${escapeHtml(s.priority)}">${s.priority.toUpperCase()}</span>
        <h3 class="dash-snag-panel__title">${escapeHtml(s.title)}</h3>
        <button type="button" class="dash-snag-panel__close" data-role="close" aria-label="Close">×</button>
      </header>
      ${s.customerName ? `<p class="dash-snag-panel__cust"><strong>Customer:</strong> ${escapeHtml(s.customerName)}</p>` : ""}
      <div class="dash-snag-panel__body">${s.content ? escapeHtml(s.content).replace(/\n/g, "<br>") : "<em>No further details.</em>"}</div>
      <footer class="dash-snag-panel__foot">
        <button type="button" class="dash-btn dash-btn--ghost" data-role="close">Close</button>
        <button type="button" class="dash-btn dash-btn--primary" data-role="resolve">Mark as resolved</button>
      </footer>
    `;
    for (const b of panel.querySelectorAll<HTMLButtonElement>("[data-role='close']")) {
      b.addEventListener("click", closePopup);
    }
    panel.querySelector<HTMLButtonElement>("[data-role='resolve']")?.addEventListener("click", () => {
      void resolveSnag(s.id);
    });
  }

  function openCreate(): void {
    closePopup();
    const { panel } = buildOverlay();
    // Customer Name uses the same CRM-contact picker pattern as calendar /
    // bookings / compose-message. NAME ONLY (no masked emails), strict
    // selection (no freetext fallback — submit blocks until a CRM contact
    // is chosen). Mirror of [[feedback-retailer-phone-twilio-only]] /
    // [[project-platform-messaging-only]] discipline.
    panel.innerHTML = `
      <header class="dash-snag-panel__head">
        <h3 class="dash-snag-panel__title">New snag</h3>
        <button type="button" class="dash-snag-panel__close" data-role="close" aria-label="Close">×</button>
      </header>
      <form class="dash-snag-form" data-role="form">
        <div class="dash-snag-field dash-snag-picker" data-role="cust-picker">
          <span class="dash-snag-field__lbl">Customer</span>
          <div class="dash-snag-picker__search-wrap" data-role="cust-search-wrap">
            <input type="text" class="dash-snag-picker__search"
                   data-role="cust-search"
                   autocomplete="off"
                   role="combobox"
                   aria-autocomplete="list"
                   aria-expanded="false"
                   placeholder="Search customers by name…" />
            <div class="dash-snag-picker__results"
                 role="listbox"
                 data-role="cust-results"
                 hidden></div>
            <div class="dash-snag-picker__hint" data-role="cust-hint"></div>
          </div>
          <div class="dash-snag-picker__selected" data-role="cust-selected" hidden>
            <span class="dash-snag-picker__chip" data-role="cust-chip"></span>
            <button type="button" class="dash-snag-picker__clear" data-role="cust-clear">Change</button>
          </div>
        </div>
        <label class="dash-snag-field">
          <span class="dash-snag-field__lbl">Title</span>
          <input type="text" data-field="title" placeholder="One-line summary" required />
        </label>
        <label class="dash-snag-field">
          <span class="dash-snag-field__lbl">Details</span>
          <textarea data-field="content" rows="4" placeholder="What needs attention?"></textarea>
        </label>
        <label class="dash-snag-field">
          <span class="dash-snag-field__lbl">Priority</span>
          <select data-field="priority">
            <option value="high">High</option>
            <option value="medium" selected>Medium</option>
            <option value="low">Low</option>
          </select>
        </label>
        <footer class="dash-snag-panel__foot">
          <button type="button" class="dash-btn dash-btn--ghost" data-role="close">Cancel</button>
          <button type="submit" class="dash-btn dash-btn--primary">Add snag</button>
        </footer>
      </form>
    `;
    for (const b of panel.querySelectorAll<HTMLButtonElement>("[data-role='close']")) {
      b.addEventListener("click", closePopup);
    }

    // ── Customer picker (CRM-only, no freetext) ─────────────────────────
    interface SnagContact { id: string; name: string }
    const custSearch    = panel.querySelector<HTMLInputElement>("[data-role='cust-search']")!;
    const custWrap      = panel.querySelector<HTMLDivElement>("[data-role='cust-search-wrap']")!;
    const custResults   = panel.querySelector<HTMLDivElement>("[data-role='cust-results']")!;
    const custSelectedEl = panel.querySelector<HTMLDivElement>("[data-role='cust-selected']")!;
    const custChip      = panel.querySelector<HTMLSpanElement>("[data-role='cust-chip']")!;
    const custClearBtn  = panel.querySelector<HTMLButtonElement>("[data-role='cust-clear']")!;
    const custHint      = panel.querySelector<HTMLDivElement>("[data-role='cust-hint']")!;

    let contactCache: SnagContact[] | null = null;
    let custSelection: SnagContact | null = null;
    let custHighlight = -1;

    const escPickerAttr = (s: string): string => s
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

    const setHint = (text: string): void => { custHint.textContent = text; };

    const selectContact = (c: SnagContact): void => {
      custSelection = c;
      custWrap.hidden = true;
      custResults.hidden = true;
      custSearch.setAttribute("aria-expanded", "false");
      custSelectedEl.hidden = false;
      custChip.textContent = c.name;
      setHint("");
    };

    const clearContactSelection = (): void => {
      custSelection = null;
      custSearch.value = "";
      custWrap.hidden = false;
      custSelectedEl.hidden = true;
      custResults.hidden = true;
      custResults.innerHTML = "";
      custSearch.setAttribute("aria-expanded", "false");
      setHint("");
      custHighlight = -1;
    };

    const renderResults = (query: string): void => {
      const list = contactCache ?? [];
      const q = query.trim().toLowerCase();
      if (!q && document.activeElement !== custSearch) {
        custResults.innerHTML = "";
        custResults.hidden = true;
        custSearch.setAttribute("aria-expanded", "false");
        setHint(
          list.length === 0
            ? "No customers yet — add one in the CRM first."
            : `${list.length} customer${list.length === 1 ? "" : "s"} available — start typing.`,
        );
        custHighlight = -1;
        return;
      }
      const matches = q ? list.filter((c) => c.name.toLowerCase().includes(q)) : list;
      const visible = matches.slice(0, 20);
      if (visible.length === 0) {
        custResults.innerHTML = "";
        custResults.hidden = true;
        custSearch.setAttribute("aria-expanded", "false");
        setHint(list.length === 0 ? "No customers yet — add one in the CRM first." : "No matches.");
        custHighlight = -1;
        return;
      }
      custHighlight = 0;
      custResults.innerHTML = visible.map((c, i) => `
        <button type="button" class="dash-snag-picker__option${i === custHighlight ? " is-active" : ""}"
                role="option" data-cid="${escPickerAttr(c.id)}"
                aria-selected="${i === custHighlight ? "true" : "false"}">
          <span class="dash-snag-picker__option-name">${escPickerAttr(c.name)}</span>
        </button>
      `).join("");
      custResults.hidden = false;
      custSearch.setAttribute("aria-expanded", "true");
      setHint(matches.length > visible.length
        ? `Showing ${visible.length} of ${matches.length} — keep typing to narrow.`
        : "");
    };

    const applyHighlight = (els: NodeListOf<HTMLButtonElement>): void => {
      els.forEach((el, i) => {
        el.classList.toggle("is-active", i === custHighlight);
        el.setAttribute("aria-selected", i === custHighlight ? "true" : "false");
        if (i === custHighlight) el.scrollIntoView({ block: "nearest" });
      });
    };

    void (async (): Promise<void> => {
      setHint("Loading…");
      try {
        interface WireCustomer { id?: string; name?: string }
        const resp = await api<{ contacts?: WireCustomer[] }>("/crm/contacts");
        const list = (Array.isArray(resp?.contacts) ? resp.contacts : [])
          .map((r): SnagContact => ({
            id: String(r.id ?? "").trim(),
            name: String(r.name ?? "").trim(),
          }))
          .filter((r) => r.id && r.name);
        contactCache = list;
        if (list.length === 1 && !custSelection) {
          const only = list[0];
          if (only) { selectContact(only); return; }
        }
        renderResults(custSearch.value);
      } catch {
        setHint("Could not load customers. Try again shortly.");
      }
    })();

    custSearch.addEventListener("input", () => renderResults(custSearch.value));
    custSearch.addEventListener("focus", () => renderResults(custSearch.value));
    custSearch.addEventListener("keydown", (e: KeyboardEvent) => {
      const optionEls = custResults.querySelectorAll<HTMLButtonElement>(".dash-snag-picker__option");
      if (optionEls.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        custHighlight = (custHighlight + 1) % optionEls.length;
        applyHighlight(optionEls);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        custHighlight = (custHighlight - 1 + optionEls.length) % optionEls.length;
        applyHighlight(optionEls);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const chosen = optionEls[custHighlight] ?? optionEls[0];
        chosen?.click();
      } else if (e.key === "Escape") {
        if (!custResults.hidden) {
          e.preventDefault();
          custResults.hidden = true;
          custSearch.setAttribute("aria-expanded", "false");
        }
      }
    });
    custResults.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(".dash-snag-picker__option");
      if (!btn) return;
      const cid = btn.dataset["cid"];
      const match = (contactCache ?? []).find((c) => c.id === cid);
      if (match) selectContact(match);
    });
    custClearBtn.addEventListener("click", () => {
      clearContactSelection();
      setTimeout(() => custSearch.focus(), 0);
    });

    const form = panel.querySelector<HTMLFormElement>("[data-role='form']");
    form?.addEventListener("submit", (e) => {
      e.preventDefault();
      const titleInput = form.querySelector<HTMLInputElement>("[data-field='title']");
      const title = titleInput?.value.trim() ?? "";
      if (!title) {
        titleInput?.focus();
        return;
      }
      if (!custSelection) {
        setHint("Pick a customer from the list before adding the snag.");
        custSearch.focus();
        return;
      }
      const payload = {
        title,
        customerName: custSelection.name,
        content: form.querySelector<HTMLTextAreaElement>("[data-field='content']")?.value.trim() ?? "",
        priority:
          (form.querySelector<HTMLSelectElement>("[data-field='priority']")?.value as SnagPriority) ?? "medium",
      };
      void createSnag(payload);
    });
    custSearch.focus();
  }

  async function createSnag(payload: {
    title: string;
    customerName: string;
    content: string;
    priority: SnagPriority;
  }): Promise<void> {
    try {
      const res = await api<{ ok: boolean; snag: SnagRow }>(`/portal/snags`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "content-type": "application/json" },
      });
      if (!res.snag) return;
      items = [res.snag, ...items];
      paint();
      closePopup();
    } catch (err) {
      console.error("[snags] create failed", err);
      ctx.showToast("Couldn't add snag — try again", "error");
    }
  }

  async function resolveSnag(id: string): Promise<void> {
    const prev = items;
    items = items.filter((x) => x.id !== id);
    paint();
    closePopup();
    try {
      await api<{ ok: boolean }>(`/portal/snags/${encodeURIComponent(id)}/resolve`, {
        method: "POST",
      });
    } catch (err) {
      console.error("[snags] resolve failed", err);
      items = prev;
      paint();
      ctx.showToast("Couldn't mark as resolved — try again", "error");
    }
  }

  addBtn.addEventListener("click", () => openCreate());
  paint();
  void load();

  return {
    cleanup(): void {
      destroyed = true;
      closePopup();
    },
  };
}

const SUPPLIER_SCOPE_LS = "kos_supplier_offers_scope_v1";
function readSupplierScope(): "mine" | "all" {
  try {
    return localStorage.getItem(SUPPLIER_SCOPE_LS) === "all" ? "all" : "mine";
  } catch {
    return "mine";
  }
}

function writeSupplierScope(v: "mine" | "all"): void {
  try { localStorage.setItem(SUPPLIER_SCOPE_LS, v); } catch { /* ignore */ }
}

/** Open a centred overlay rendering a supplier post's full HTML body.
 *  Used by both Supplier Offers and Supplier News widgets — retailer stays
 *  on the dashboard. The server sanitises contentHtml on write
 *  (supplier_offers_news.js → `sanitise()`); we ALSO sanitise here via the
 *  shared DOMPurify allowlist (belt-and-braces) before touching innerHTML. */
function openSupplierContentPopup(opts: {
  supplierName: string;
  supplierColor?: string;
  headline: string;
  subtitle?: string;
  html: string;
}): void {
  const modal = createModal({
    title: opts.headline,
    subtitle: opts.supplierName + (opts.subtitle ? ` · ${opts.subtitle}` : ""),
    size: "lg",
    content: (body: HTMLElement): void => {
      const wrap = document.createElement("div");
      wrap.className = "dash-content-popup";
      // Belt-and-braces client sanitise (shared DOMPurify allowlist) on top of
      // the server's write-time sanitise, before assigning innerHTML.
      wrap.innerHTML = sanitiseRichHtml(opts.html) || "<p><em>No content.</em></p>";
      body.appendChild(wrap);
    },
    actions: [{ label: "Close", variant: "primary", closeOnClick: true }],
    onClose: (): void => { window.setTimeout(() => modal.destroy(), 300); },
  });
  modal.open();
}

function connectedSupplierCodes(): string[] {
  const out: Record<string, true> = {};
  const add = (v: unknown): void => {
    if (!v) return;
    if (Array.isArray(v)) { v.forEach(add); return; }
    if (typeof v === "object") {
      const o = v as Record<string, unknown>;
      Object.keys(o).forEach((k) => { if (o[k]) out[String(k).toUpperCase()] = true; });
      return;
    }
    out[String(v).toUpperCase()] = true;
  };
  const keys = [
    "kos_onboarding_suppliers",
    "kos_connected_suppliers",
    "kos_onboarding_v1",
    "kos_suppliers_v1",
  ];
  for (const k of keys) {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      let parsed: unknown;
      try { parsed = JSON.parse(raw); } catch { parsed = raw; }
      if (parsed && typeof parsed === "object") {
        const p = parsed as { suppliers?: unknown; connected?: unknown };
        if (Array.isArray(p.suppliers)) add(p.suppliers);
        else if (Array.isArray(p.connected)) add(p.connected);
        else add(parsed);
      } else {
        add(parsed);
      }
    } catch { /* ignore */ }
  }
  if (!Object.keys(out).length) {
    ["TKC", "PWS", "OMEGA", "BLUM", "HETTICH"].forEach((k) => { out[k] = true; });
  }
  return Object.keys(out);
}

function supplierIsMine(code: string): boolean {
  const c = String(code || "").toUpperCase();
  const mine = connectedSupplierCodes();
  return mine.some((m) => m === c || m.indexOf(c) === 0 || c.indexOf(m) === 0);
}

function offerRowHtml(o: SupplierOffer): string {
  const chip =
    '<span style="width:34px;height:34px;border-radius:8px;background:' +
    escapeHtml(o.supplierColor || "#3b82f6") +
    ';color:#fff;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:800;flex-shrink:0">' +
    escapeHtml(o.supplierName) +
    "</span>";
  const body =
    '<div style="flex:1;min-width:0">' +
    '<div style="font-size:12px;font-weight:600;color:var(--text)">' + escapeHtml(o.title) + "</div>" +
    '<div style="font-size:10px;color:var(--text-muted)">' + escapeHtml(formatExpires(o.validUntil)) + "</div>" +
    "</div>";
  const badge = o.badge
    ? '<span style="font-size:9px;font-weight:800;background:var(--orange);color:#fff;padding:2px 6px;border-radius:4px;flex-shrink:0">' +
      escapeHtml(o.badge) + "</span>"
    : "";
  // The whole row is clickable — opens a popup overlay with the offer's
  // full HTML body. The legacy CTA button is preserved as a secondary
  // action when the offer carries one (e.g. an external claim link).
  const cta = o.cta
    ? '<button type="button" data-role="offer-cta" data-offer-id="' + escapeHtml(o.id) +
      '" style="font-size:10px;font-weight:700;padding:4px 10px;border-radius:6px;background:var(--surface2);color:var(--text2);border:1px solid var(--border);cursor:pointer;flex-shrink:0">' +
      escapeHtml(o.cta) + "</button>"
    : "";
  return (
    '<div class="dash-offer-row" role="button" tabindex="0" data-role="offer-open" ' +
    'data-offer-id="' + escapeHtml(o.id) + '" ' +
    'style="display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid var(--border);cursor:pointer">' +
    chip + body + badge + cta +
    "</div>"
  );
}

function renderOffers(
  host: HTMLElement,
  all: SupplierOffer[],
  scope: "mine" | "all",
): void {
  const filtered = scope === "all"
    ? all.slice()
    : all.filter((o) => supplierIsMine(o.supplierName) || supplierIsMine(o.supplierId));
  if (filtered.length === 0) {
    if (scope === "mine") {
      host.innerHTML =
        '<div class="kos-sof-empty" style="font-size:12px;color:var(--text-muted);padding:14px 2px">' +
        'No current offers from your connected suppliers. ' +
        '<a href="#" data-role="offers-connect-more" style="color:var(--orange);text-decoration:none">Connect more</a> ' +
        'or switch to <b>Browse all offers</b>.' +
        '</div>';
    } else {
      host.innerHTML =
        '<div class="kos-sof-empty" style="font-size:12px;color:var(--text-muted);padding:14px 2px">' +
        'No live supplier offers right now.' +
        '</div>';
    }
    return;
  }
  host.innerHTML = filtered.map(offerRowHtml).join("");
}

function newsRowHtml(n: SupplierNewsItem): string {
  const chip =
    '<span style="width:34px;height:34px;border-radius:8px;background:' +
    escapeHtml(n.supplierColor || "#3b82f6") +
    ';color:#fff;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:800;flex-shrink:0">' +
    escapeHtml(n.supplierName) +
    "</span>";
  const date = n.publishedAt
    ? new Date(n.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
    : "";
  const body =
    '<div style="flex:1;min-width:0">' +
    '<div style="font-size:12px;font-weight:600;color:var(--text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' +
    escapeHtml(n.headline || "Untitled") + "</div>" +
    '<div style="font-size:10px;color:var(--text-muted)">' +
    escapeHtml(n.supplierName) + (date ? " · " + escapeHtml(date) : "") + "</div>" +
    "</div>";
  return (
    '<div class="dash-news-row" role="button" tabindex="0" data-role="news-open" ' +
    'data-news-id="' + escapeHtml(n.id) + '" ' +
    'style="display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid var(--border);cursor:pointer">' +
    chip + body +
    "</div>"
  );
}

function renderNews(
  host: HTMLElement,
  all: SupplierNewsItem[],
  scope: "mine" | "all",
): void {
  const filtered = scope === "all"
    ? all.slice()
    : all.filter((n) => supplierIsMine(n.supplierName) || supplierIsMine(n.supplierId));
  if (filtered.length === 0) {
    host.innerHTML =
      '<div class="kos-sof-empty" style="font-size:12px;color:var(--text-muted);padding:14px 2px">' +
      'No supplier news yet.' +
      '</div>';
    return;
  }
  host.innerHTML = filtered.map(newsRowHtml).join("");
}

const REVIEW_AVATAR_COLORS = ["#e58200", "#3b82f6", "#22c55e", "#a855f7", "#ef4444", "#0ea5e9", "#f59e0b", "#ec4899"];

function reviewStarSvg(filled: boolean): string {
  return `<svg viewBox="0 0 24 24" style="width:12px;height:12px;display:inline-block;vertical-align:middle;fill:${filled ? "#fbbc04" : "rgba(255,255,255,.18)"};stroke:none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77 5.82 21l1.18-6.88-5-4.87 6.91-1.01L12 2z"/></svg>`;
}

function reviewAvatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return REVIEW_AVATAR_COLORS[Math.abs(h) % REVIEW_AVATAR_COLORS.length]!;
}

function reviewInitials(name: string): string {
  const p = name.trim().split(/\s+/);
  return (p.length === 1 ? p[0]!.substring(0, 2) : p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

function renderReviews(host: HTMLElement, r: GoogleReviewBlock): void {
  const stars = (n: number): string => {
    let out = "";
    for (let i = 1; i <= 5; i++) out += reviewStarSvg(i <= Math.round(n));
    return out;
  };

  const reviewCards = r.reviews
    .map((rv) => {
      let rs = "";
      for (let i = 1; i <= 5; i++) rs += reviewStarSvg(i <= rv.rating);
      return (
        `<div style="padding:10px 12px;border-bottom:1px solid var(--border);display:flex;gap:8px;align-items:flex-start">` +
          `<div style="width:28px;height:28px;border-radius:50%;background:${reviewAvatarColor(rv.name)};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:11px;flex-shrink:0">${escapeHtml(reviewInitials(rv.name))}</div>` +
          `<div style="flex:1;min-width:0">` +
            `<div style="display:flex;justify-content:space-between;gap:6px;align-items:center">` +
              `<div style="font-weight:700;font-size:11.5px;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(rv.name)}</div>` +
              `<div style="font-size:10px;color:var(--text-muted);white-space:nowrap">${escapeHtml(rv.date)}</div>` +
            `</div>` +
            `<div style="margin:2px 0 3px;display:flex;gap:1px">${rs}</div>` +
            `<div style="font-size:11px;line-height:1.45;color:var(--text2);overflow-wrap:anywhere">${escapeHtml(rv.text)}</div>` +
          `</div>` +
        `</div>`
      );
    })
    .join("");

  host.style.padding = "0";
  host.innerHTML =
    `<div style="padding:10px 12px;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:10px">` +
      `<div style="font-size:26px;font-weight:800;color:var(--text);line-height:1">${r.rating.toFixed(1)}</div>` +
      `<div style="flex:1;min-width:0">` +
        `<div style="display:flex;gap:2px;margin-bottom:2px">${stars(r.rating)}</div>` +
        `<div style="font-size:10px;color:var(--text-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${r.count} reviews</div>` +
      `</div>` +
      `<a href="https://g.page/r/kitchenos/review" target="_blank" rel="noopener" style="font-size:10px;font-weight:700;color:var(--orange);text-decoration:none;padding:5px 8px;border:1px solid var(--border);border-radius:7px;white-space:nowrap">Ask</a>` +
      `<button data-role="gbp-post-update" style="font-size:10px;font-weight:700;color:#4285f4;background:none;border:1px solid var(--border);border-radius:7px;padding:5px 8px;cursor:pointer;white-space:nowrap;margin-left:4px">Post Update</button>` +
    `</div>` +
    `<div id="w-google-reviews-list" style="max-height:340px;overflow-y:auto">${reviewCards}</div>`;
}

// ── Links widgets — localStorage-backed (frontend-only) ──────────────────────
// Custom "My Links" + the "Standard Links" categories persist per-device in
// localStorage (seeded from the aggregator / hardcoded defaults on first use).
// No backend write endpoint exists; these are personal productivity bookmarks.
const MYLINKS_LS = "tkos.dashboard.mylinks";
const STDLINKS_LS = "tkos.dashboard.stdlinks";

interface StoredLink { id: string; name: string; url: string; }

function normaliseUrl(u: string): string {
  const t = u.trim();
  if (!t) return "";
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
}

type ApiFn = PageContext["api"];

// One-time migration of legacy localStorage links into the PG-backed endpoints,
// so retailers who'd already added links don't lose them on the switch to
// cross-device sync. Runs only when the backend has none yet; clears the key
// afterwards.
async function migrateMyLinksFromLocalStorage(api: ApiFn): Promise<void> {
  let raw: string | null = null;
  try { raw = localStorage.getItem(MYLINKS_LS); } catch { raw = null; }
  if (!raw) return;
  try {
    const cur = await api<{ links?: unknown[] }>("/portal/links");
    if (Array.isArray(cur.links) && cur.links.length > 0) { localStorage.removeItem(MYLINKS_LS); return; }
    const list = JSON.parse(raw) as Array<{ name?: string; url?: string }>;
    if (Array.isArray(list)) {
      for (const l of list) {
        if (l && l.name && l.url) {
          await api("/portal/links", { method: "POST", body: JSON.stringify({ label: l.name, url: l.url }) });
        }
      }
    }
    localStorage.removeItem(MYLINKS_LS);
  } catch { /* leave the key for a later attempt */ }
}
async function migrateStdLinksFromLocalStorage(api: ApiFn): Promise<void> {
  let raw: string | null = null;
  try { raw = localStorage.getItem(STDLINKS_LS); } catch { raw = null; }
  if (!raw) return;
  try {
    const cur = await api<{ categories?: unknown }>("/portal/links/standard");
    if (Array.isArray(cur.categories) && cur.categories.length > 0) { localStorage.removeItem(STDLINKS_LS); return; }
    const cats = JSON.parse(raw);
    if (Array.isArray(cats) && cats.length > 0) {
      await api("/portal/links/standard", { method: "POST", body: JSON.stringify({ categories: cats }) });
    }
    localStorage.removeItem(STDLINKS_LS);
  } catch { /* leave the key for a later attempt */ }
}

interface MyLinkHandlers { onEdit: (id: string) => void; onDelete: (id: string) => void; }

function renderMyLinks(host: HTMLElement, links: StoredLink[], handlers: MyLinkHandlers): void {
  if (links.length === 0) {
    host.innerHTML = `<li class="dash-empty">No links yet — use “+ Add Link”.</li>`;
    return;
  }
  host.innerHTML = links
    .map((l) => {
      const domain = domainFromUrl(l.url);
      const initial = (l.name.trim()[0] ?? "?").toUpperCase();
      const bg = bookmarkColor(l.name || l.url);
      return `
        <li class="dash-link" data-id="${escapeHtml(l.id)}">
          <a class="dash-link__inner" href="${escapeHtml(l.url)}" target="_blank" rel="noopener noreferrer">
            <span class="dash-link__fav" style="background:${bg};color:#fff">${escapeHtml(initial)}</span>
            <span class="dash-link__text">
              <span class="dash-link__name">${escapeHtml(l.name)}</span>
              <span class="dash-link__domain">${escapeHtml(domain)}</span>
            </span>
          </a>
          <span class="dash-link__actions">
            <button type="button" class="dash-link__act" data-edit aria-label="Edit link">${iconMarkup("pencil", { size: "xs" })}</button>
            <button type="button" class="dash-link__act" data-del aria-label="Delete link">${iconMarkup("trash", { size: "xs" })}</button>
          </span>
        </li>`;
    })
    .join("");
  host.querySelectorAll<HTMLElement>(".dash-link").forEach((li) => {
    const id = li.dataset["id"] ?? "";
    li.querySelector<HTMLButtonElement>("[data-edit]")?.addEventListener("click", (e) => { e.preventDefault(); handlers.onEdit(id); });
    li.querySelector<HTMLButtonElement>("[data-del]")?.addEventListener("click", (e) => { e.preventDefault(); handlers.onDelete(id); });
  });
}

// Add / edit a single custom link (label + URL).
function openLinkEditor(existing: StoredLink | null, onSubmit: (vals: { name: string; url: string }) => void): void {
  const modal = createModal({
    title: existing ? "Edit link" : "Add link",
    size: "sm",
    content: (body) => {
      body.innerHTML = `
        <div class="dash-linkform">
          <label>Label<input type="text" data-f="name" value="${existing ? escapeHtml(existing.name) : ""}" placeholder="e.g. Supplier portal" /></label>
          <label>URL<input type="url" data-f="url" value="${existing ? escapeHtml(existing.url) : ""}" placeholder="https://…" /></label>
          <p class="dash-linkform__err" data-role="err" hidden></p>
        </div>`;
    },
    actions: [
      { label: "Cancel", variant: "ghost" },
      {
        label: existing ? "Save" : "Add",
        variant: "primary",
        closeOnClick: false,
        onClick: () => {
          const root = modal.el;
          const name = root.querySelector<HTMLInputElement>('[data-f="name"]')?.value.trim() ?? "";
          const url = normaliseUrl(root.querySelector<HTMLInputElement>('[data-f="url"]')?.value ?? "");
          const err = root.querySelector<HTMLElement>('[data-role="err"]');
          if (!name || !url) { if (err) { err.textContent = "Both a label and URL are required."; err.hidden = false; } return; }
          onSubmit({ name, url });
          modal.close();
        },
      },
    ],
  });
  modal.open();
}

// Manage the Standard Links categories — edit / add / remove links + Save.
function openStdLinksManager(
  cats: StdLinkCategory[],
  defaults: StdLinkCategory[],
  onSave: (next: StdLinkCategory[]) => void,
): void {
  const itemRow = (name: string, url: string): string => `
    <div class="dash-stdmgr__row">
      <input type="text" data-n value="${escapeHtml(name)}" placeholder="Name" />
      <input type="url" data-u value="${escapeHtml(url)}" placeholder="https://…" />
      <button type="button" class="dash-stdmgr__rm" data-rm aria-label="Remove">${iconMarkup("close", { size: "xs" })}</button>
    </div>`;
  const catBlock = (c: StdLinkCategory): string => `
    <div class="dash-stdmgr__cat" data-key="${escapeHtml(c.key)}" data-label="${escapeHtml(c.label)}">
      <div class="dash-stdmgr__catlbl">${escapeHtml(c.label)}</div>
      <div class="dash-stdmgr__items" data-items>${c.items.map((it) => itemRow(it.name, it.url)).join("")}</div>
      <button type="button" class="dash-stdmgr__additem" data-add-item>+ Add link</button>
    </div>`;
  const buildBody = (source: StdLinkCategory[]): string =>
    `<div class="dash-stdmgr">${source.map(catBlock).join("")}
      <button type="button" class="dash-stdmgr__reset" data-reset>Reset to defaults</button></div>`;

  const modal = createModal({
    title: "Manage standard links",
    size: "lg",
    content: (body) => {
      body.innerHTML = buildBody(cats);
      // Delegated controls within the manager.
      body.addEventListener("click", (e) => {
        const t = e.target as HTMLElement;
        const rm = t.closest<HTMLButtonElement>("[data-rm]");
        if (rm) { rm.closest(".dash-stdmgr__row")?.remove(); return; }
        const add = t.closest<HTMLButtonElement>("[data-add-item]");
        if (add) {
          const items = add.previousElementSibling;
          if (items) items.insertAdjacentHTML("beforeend", itemRow("", ""));
          return;
        }
        const reset = t.closest<HTMLButtonElement>("[data-reset]");
        if (reset) { body.innerHTML = buildBody(defaults); return; }
      });
    },
    actions: [
      { label: "Cancel", variant: "ghost" },
      {
        label: "Save",
        variant: "primary",
        onClick: () => {
          const root = modal.el;
          const next: StdLinkCategory[] = [];
          root.querySelectorAll<HTMLElement>(".dash-stdmgr__cat").forEach((catEl) => {
            const items: { name: string; domain: string; url: string }[] = [];
            catEl.querySelectorAll<HTMLElement>(".dash-stdmgr__row").forEach((row) => {
              const name = row.querySelector<HTMLInputElement>("[data-n]")?.value.trim() ?? "";
              const url = normaliseUrl(row.querySelector<HTMLInputElement>("[data-u]")?.value ?? "");
              if (name && url) items.push({ name, domain: domainFromUrl(url), url });
            });
            next.push({ key: catEl.dataset["key"] ?? "", label: catEl.dataset["label"] ?? "", items });
          });
          onSave(next);
        },
      },
    ],
  });
  modal.open();
}

// ── Courier widget ───────────────────────────────────────────────────────────

interface CourierBrand {
  name: string;
  color: string;
  url: string;
  letter: string;
  dark?: boolean;
}

const COURIERS: Record<CourierCode, CourierBrand> = {
  dpd:         { name: "DPD",           color: "#dc0032", url: "https://track.dpd.co.uk/parcels/",                                          letter: "D" },
  dhl:         { name: "DHL",           color: "#ffcc00", url: "https://www.dhl.com/gb-en/home/tracking.html?tracking-id=",                  letter: "H", dark: true },
  royalmail:   { name: "Royal Mail",    color: "#d40511", url: "https://www.royalmail.com/track-your-item?trackNumber=",                     letter: "R" },
  parcelforce: { name: "Parcelforce",   color: "#004c89", url: "https://www.parcelforce.com/track-trace?trackNumber=",                       letter: "P" },
  apc:         { name: "APC Overnight", color: "#e30613", url: "https://apc-overnight.com/receiving-a-parcel/track-your-parcel?movementRef=", letter: "A" },
  evri:        { name: "Evri",          color: "#ff1e90", url: "https://www.evri.com/track/parcel/",                                          letter: "E" },
};

function detectCourier(tn: string | null | undefined): CourierCode | null {
  if (!tn) return null;
  const s = String(tn).replace(/\s+/g, "").toUpperCase();
  if (/^\d{14}$/.test(s))                                     return "dpd";
  if (/^\d{10}$/.test(s))                                     return "dhl";
  if (/^JD[0-9]{18}$/.test(s))                                return "dhl";
  if (/^[A-Z]{2}\d{9}GB$/.test(s))                            return "royalmail";
  if (/^(PB|PH|EK|CJ|JJ|ZW)\d{9}GB?$/.test(s))                return "parcelforce";
  if (/^AP[A-Z0-9]{8,12}$/.test(s))                           return "apc";
  if (/^\d{12}$/.test(s))                                     return "apc";
  if (/^H[0-9A-Z]{14,20}$/.test(s))                           return "evri";
  if (/^T\d{10,14}$/.test(s))                                 return "evri";
  if (/^\d{16,18}$/.test(s))                                  return "evri";
  return null;
}

interface CourierStatusMeta {
  label: string;
  color: string;
  icon: string;
}

const STATUS: Record<CourierStatus, CourierStatusMeta> = {
  collected:        { label: "Collected",        color: "#64748b", icon: iconMarkup("check",   { size: "xs" }) },
  in_transit:       { label: "In Transit",       color: "#3b82f6", icon: iconMarkup("truck",   { size: "xs" }) },
  out_for_delivery: { label: "Out for Delivery", color: "#e58200", icon: iconMarkup("truck",   { size: "xs" }) },
  delivered:        { label: "Delivered",        color: "#22c55e", icon: iconMarkup("success-circle", { size: "xs" }) },
};

function etaLabel(days: number): string {
  if (days < 0)   return `Delivered ${Math.abs(days)}d ago`;
  if (days === 0) return "Arriving today";
  if (days === 1) return "Arriving tomorrow";
  return `Arriving in ${days} days`;
}

function courierRowHtml(entry: CourierDelivery): string {
  const code = detectCourier(entry.tracking);
  const c: CourierBrand = code
    ? COURIERS[code]
    : { name: "Unknown", color: "#64748b", url: "", letter: "?" };
  const st = STATUS[entry.status] || STATUS.in_transit;
  const trackUrl = c.url ? c.url + encodeURIComponent(entry.tracking) : "#";
  // The TKOS icon inherits its colour from `currentColor`; the outer status
  // pill already sets `color: ${st.color}`, so the icon picks it up.
  return (
    '<div style="display:flex;gap:12px;align-items:center;padding:10px 12px;background:var(--surface);border:1px solid var(--border);border-radius:10px;margin-bottom:8px">' +
      '<div style="width:42px;height:42px;border-radius:10px;background:' + c.color + ';color:' + (c.dark ? "#111" : "#fff") + ';display:flex;align-items:center;justify-content:center;font-weight:800;font-size:16px;flex-shrink:0">' + escapeHtml(c.letter) + '</div>' +
      '<div style="flex:1;min-width:0">' +
        '<div style="display:flex;justify-content:space-between;gap:8px;align-items:center;flex-wrap:wrap">' +
          '<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">' +
            '<span style="font-weight:700;font-size:12px;color:var(--text)">' + escapeHtml(entry.ref) + '</span>' +
            '<span style="font-size:10px;color:var(--text-muted)">·</span>' +
            '<span style="font-size:11px;color:var(--text2);cursor:pointer" data-customer-name="' + escapeHtml(entry.customer) + '">' + escapeHtml(entry.customer) + '</span>' +
          '</div>' +
          '<span style="display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:800;background:' + st.color + '22;color:' + st.color + ';padding:3px 8px;border-radius:5px;text-transform:uppercase;letter-spacing:.5px">' +
            '<span style="display:inline-flex;align-items:center;justify-content:center">' + st.icon + '</span>' +
            escapeHtml(st.label) +
          '</span>' +
        '</div>' +
        '<div style="font-size:11px;color:var(--text-muted);margin-top:3px;font-family:ui-monospace,Menlo,monospace">' + escapeHtml(c.name) + ' · ' + escapeHtml(entry.tracking) + '</div>' +
        '<div style="font-size:11px;color:var(--text2);margin-top:2px">' + escapeHtml(etaLabel(entry.etaDays)) + (entry.note ? ' · ' + escapeHtml(entry.note) : '') + '</div>' +
      '</div>' +
      (c.url ? '<a href="' + escapeHtml(trackUrl) + '" target="_blank" rel="noopener" style="font-size:10px;font-weight:700;color:var(--orange);text-decoration:none;padding:6px 10px;border:1px solid var(--border);border-radius:8px;white-space:nowrap">Track</a>' : '') +
    '</div>'
  );
}

function renderCouriers(host: HTMLElement, list: CourierDelivery[]): void {
  host.innerHTML = list.map(courierRowHtml).join("");
}

function randomKosRef(): string {
  const part = (): string =>
    Math.random().toString(36).toUpperCase().replace(/[^A-Z0-9]/g, "");
  return "KOS-" + (part() + part()).slice(0, 12);
}

// ── Social AI widget ─────────────────────────────────────────────────────────
// The widget mounts the full Social Media AI page module so the experience
// is identical to /social-ai. See socialAiCleanup in the dashboard mount.

// ── Quick Notes ─────────────────────────────────────────────────────────────
// Apple-Notes-style list widget backed by /api/portal/quick-notes. Each note
// is a card with a title input + body textarea + delete button. + Add note in
// the header creates a blank note immediately and focuses the title field;
// edits debounce-save 350ms after the last keystroke; delete is optimistic.

interface QuickNote {
  id: string;
  title: string | null;
  body: string;
  createdAt: number;
  updatedAt: number;
}

interface NotesHandle {
  cleanup: () => void;
}

function mountQuickNotes(
  listHost: HTMLElement,
  addBtn: HTMLButtonElement,
  ctx: PageContext,
): NotesHandle {
  let notes: QuickNote[] = [];
  let destroyed = false;
  const saveTimers = new Map<string, ReturnType<typeof setTimeout>>();

  function emptyMarkup(): string {
    return `<li class="dash-empty">No notes yet — tap “+ Add note”.</li>`;
  }

  function noteMarkup(n: QuickNote): string {
    const titleAttr = n.title == null ? "" : escapeHtml(n.title);
    return `
      <li class="dash-note" data-id="${escapeHtml(n.id)}">
        <input class="dash-note__title" type="text" placeholder="Title"
          aria-label="Note title" value="${titleAttr}" data-role="note-title" />
        <textarea class="dash-note__body" placeholder="Write something…"
          rows="3" aria-label="Note body" data-role="note-body">${escapeHtml(n.body)}</textarea>
        <button type="button" class="dash-note__rm" data-role="note-rm"
          aria-label="Delete note" title="Delete note">×</button>
      </li>`;
  }

  function paint(): void {
    if (notes.length === 0) {
      listHost.innerHTML = emptyMarkup();
      return;
    }
    listHost.innerHTML = notes.map(noteMarkup).join("");
    wireRows();
  }

  function scheduleSave(id: string): void {
    const prev = saveTimers.get(id);
    if (prev) clearTimeout(prev);
    saveTimers.set(
      id,
      setTimeout(() => {
        saveTimers.delete(id);
        void persistNote(id);
      }, 350),
    );
  }

  async function persistNote(id: string): Promise<void> {
    const n = notes.find((x) => x.id === id);
    if (!n) return;
    try {
      await api<{ ok: boolean }>(`/portal/quick-notes/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: JSON.stringify({ title: n.title, body: n.body }),
        headers: { "content-type": "application/json" },
      });
    } catch (err) {
      console.error("[quick-notes] save failed", err);
      ctx.showToast("Note couldn't be saved — retrying when online", "error");
    }
  }

  function wireRows(): void {
    for (const row of listHost.querySelectorAll<HTMLElement>("li.dash-note")) {
      const id = row.dataset["id"] ?? "";
      const titleEl = row.querySelector<HTMLInputElement>("[data-role='note-title']");
      const bodyEl = row.querySelector<HTMLTextAreaElement>("[data-role='note-body']");
      const rmEl = row.querySelector<HTMLButtonElement>("[data-role='note-rm']");
      titleEl?.addEventListener("input", () => {
        const n = notes.find((x) => x.id === id);
        if (!n) return;
        n.title = titleEl.value;
        scheduleSave(id);
      });
      bodyEl?.addEventListener("input", () => {
        const n = notes.find((x) => x.id === id);
        if (!n) return;
        n.body = bodyEl.value;
        scheduleSave(id);
      });
      rmEl?.addEventListener("click", () => void deleteNote(id));
    }
  }

  async function deleteNote(id: string): Promise<void> {
    const prev = notes;
    notes = notes.filter((n) => n.id !== id);
    paint();
    try {
      await api<{ ok: boolean }>(`/portal/quick-notes/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
    } catch (err) {
      console.error("[quick-notes] delete failed", err);
      // Roll back optimistic removal
      notes = prev;
      paint();
      ctx.showToast("Couldn't delete note — try again", "error");
    }
  }

  async function addNote(): Promise<void> {
    try {
      const res = await api<{ ok: boolean; note: QuickNote }>(`/portal/quick-notes`, {
        method: "POST",
        body: JSON.stringify({ title: "", body: "" }),
        headers: { "content-type": "application/json" },
      });
      if (!res.note) return;
      notes = [res.note, ...notes];
      paint();
      // Focus the new note's title field
      const firstTitle = listHost.querySelector<HTMLInputElement>(
        "li.dash-note:first-child [data-role='note-title']",
      );
      firstTitle?.focus();
    } catch (err) {
      console.error("[quick-notes] create failed", err);
      ctx.showToast("Couldn't add note — try again", "error");
    }
  }

  async function load(): Promise<void> {
    try {
      const res = await api<{ ok: boolean; notes: QuickNote[] }>(`/portal/quick-notes`);
      if (destroyed) return;
      notes = Array.isArray(res.notes) ? res.notes : [];
      paint();
    } catch (err) {
      console.error("[quick-notes] load failed", err);
      if (!destroyed) listHost.innerHTML = `<li class="dash-empty">Notes unavailable — retrying…</li>`;
    }
  }

  addBtn.addEventListener("click", () => void addNote());
  paint();
  void load();

  return {
    cleanup(): void {
      destroyed = true;
      for (const t of saveTimers.values()) clearTimeout(t);
      saveTimers.clear();
    },
  };
}

// ── To-Do List ──────────────────────────────────────────────────────────────
// PG-backed list widget with a Team/Personal scope toggle. Each item has a
// done checkbox + delete (×). Add input pushes into the active scope; the
// scope toggle re-queries the server. Backed by /api/portal/todos (migration
// 0012); replaces localStorage tkos.dashboard.todos.

type TodoScope = "team" | "personal";

interface TodoRow {
  id: string;
  text: string;
  done: boolean;
  scope: TodoScope;
  dueDate: string | null; // 'YYYY-MM-DD' or null
  createdAt: number;
  updatedAt: number;
}

type TodoDateState = "none" | "upcoming" | "today" | "overdue";

// Local 'YYYY-MM-DD' for "today" (date-only comparisons are string-safe in ISO).
function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function todoDateState(t: TodoRow, today: string): TodoDateState {
  if (!t.dueDate) return "none";
  if (t.done) return "upcoming"; // completed: never flag as today/overdue
  if (t.dueDate < today) return "overdue";
  if (t.dueDate === today) return "today";
  return "upcoming";
}

const TODO_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Compact label for a due-date chip, e.g. "27 Jun" (current year) or "27 Jun 27".
function fmtDueShort(iso: string): string {
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  const base = `${d} ${TODO_MONTHS[m - 1] ?? ""}`;
  const thisYear = new Date().getFullYear();
  return y === thisYear ? base : `${base} ${String(y).slice(2)}`;
}

// Custom calendar glyph (1.5px stroke, rounded caps) — no icon library.
const TODO_CAL_ICON =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M8 3v4M16 3v4M3.5 9.5h17"/></svg>';

interface TodosHandle {
  cleanup: () => void;
}

interface TodoMountOpts {
  listEl: HTMLElement;
  form: HTMLFormElement;
  input: HTMLInputElement;
  hint: HTMLElement;
  scopeBtns: HTMLButtonElement[];
  ctx: PageContext;
}

const TODO_HINTS: Record<TodoScope, string> = {
  team: "This task will be visible to your whole team.",
  personal: "Only you can see this task.",
};

function mountTodoList(opts: TodoMountOpts): TodosHandle {
  const { listEl, form, input, hint, scopeBtns, ctx } = opts;
  let scope: TodoScope = "team";
  let items: TodoRow[] = [];
  let destroyed = false;
  let loadSeq = 0;

  function paint(): void {
    if (items.length === 0) {
      listEl.innerHTML = `<li class="dash-empty">No tasks yet.</li>`;
      return;
    }
    const today = todayIso();
    // Surface overdue, then due-today, to the top; everything else keeps its
    // existing order (stable — undated tasks behave exactly as before).
    const rank = (t: TodoRow): number => {
      const st = todoDateState(t, today);
      if (st === "overdue") return 0;
      if (st === "today") return 1;
      return 2;
    };
    const ordered = items
      .map((t, i) => ({ t, i }))
      .sort((a, b) => rank(a.t) - rank(b.t) || a.i - b.i)
      .map((o) => o.t);

    listEl.innerHTML = ordered
      .map((t) => {
        const st = todoDateState(t, today);
        const dateCtl = t.dueDate
          ? `<button type="button" class="dash-todo__date dash-todo__date--${st}" data-role="todo-date" title="Change or clear date">${TODO_CAL_ICON}<span>${st === "today" ? "Today" : escapeHtml(fmtDueShort(t.dueDate))}</span></button>`
          : `<button type="button" class="dash-todo__date dash-todo__date--add" data-role="todo-date" aria-label="Set a date" title="Set a date">${TODO_CAL_ICON}</button>`;
        return `
          <li class="dash-todo dash-todo--${st}" data-id="${escapeHtml(t.id)}">
            <label class="dash-todo__lbl">
              <input type="checkbox" ${t.done ? "checked" : ""} data-role="todo-check" />
              <span class="dash-todo__text${t.done ? " is-done" : ""}">${escapeHtml(t.text)}</span>
            </label>
            ${dateCtl}
            <input type="date" class="dash-todo__dateinput" data-role="todo-dateinput" value="${t.dueDate ?? ""}" tabindex="-1" aria-hidden="true" />
            <button type="button" class="dash-todo__rm" data-role="todo-rm" aria-label="Remove task" title="Delete">×</button>
          </li>`;
      })
      .join("");
    for (const cb of listEl.querySelectorAll<HTMLInputElement>("[data-role='todo-check']")) {
      cb.addEventListener("change", () => {
        const li = cb.closest<HTMLElement>(".dash-todo");
        const id = li?.dataset["id"] ?? "";
        const t = items.find((x) => x.id === id);
        if (!t) return;
        t.done = cb.checked;
        void persist(id, { done: cb.checked });
        paint(); // re-evaluate today/overdue flags + ordering on completion
      });
    }
    for (const rm of listEl.querySelectorAll<HTMLButtonElement>("[data-role='todo-rm']")) {
      rm.addEventListener("click", () => {
        const li = rm.closest<HTMLElement>(".dash-todo");
        const id = li?.dataset["id"] ?? "";
        void deleteItem(id);
      });
    }
    // Date control: open the native picker; the hidden date input commits.
    for (const btn of listEl.querySelectorAll<HTMLButtonElement>("[data-role='todo-date']")) {
      btn.addEventListener("click", () => {
        const li = btn.closest<HTMLElement>(".dash-todo");
        const inp = li?.querySelector<HTMLInputElement>("[data-role='todo-dateinput']");
        if (!inp) return;
        const picker = inp as HTMLInputElement & { showPicker?: () => void };
        try {
          if (typeof picker.showPicker === "function") picker.showPicker();
          else inp.focus();
        } catch {
          inp.focus();
        }
      });
    }
    for (const inp of listEl.querySelectorAll<HTMLInputElement>("[data-role='todo-dateinput']")) {
      inp.addEventListener("change", () => {
        const li = inp.closest<HTMLElement>(".dash-todo");
        const id = li?.dataset["id"] ?? "";
        void setDueDate(id, inp.value);
      });
    }
  }

  async function load(): Promise<void> {
    const mySeq = ++loadSeq;
    try {
      const res = await api<{ ok: boolean; todos: TodoRow[] }>(
        `/portal/todos?scope=${encodeURIComponent(scope)}`,
      );
      if (mySeq !== loadSeq || destroyed) return;
      items = Array.isArray(res.todos) ? res.todos : [];
      paint();
    } catch (err) {
      console.error("[todos] load failed", err);
      if (destroyed || mySeq !== loadSeq) return;
      listEl.innerHTML = `<li class="dash-empty">Couldn't load tasks — retrying…</li>`;
    }
  }

  async function persist(id: string, patch: Partial<TodoRow>): Promise<void> {
    try {
      await api<{ ok: boolean }>(`/portal/todos/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: JSON.stringify(patch),
        headers: { "content-type": "application/json" },
      });
    } catch (err) {
      console.error("[todos] update failed", err);
      ctx.showToast("Couldn't save task — retry", "error");
    }
  }

  async function deleteItem(id: string): Promise<void> {
    const prev = items;
    items = items.filter((x) => x.id !== id);
    paint();
    try {
      await api<{ ok: boolean }>(`/portal/todos/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
    } catch (err) {
      console.error("[todos] delete failed", err);
      items = prev;
      paint();
      ctx.showToast("Couldn't delete task — try again", "error");
    }
  }

  async function add(text: string): Promise<void> {
    try {
      const res = await api<{ ok: boolean; todo: TodoRow }>(`/portal/todos`, {
        method: "POST",
        body: JSON.stringify({ text, scope }),
        headers: { "content-type": "application/json" },
      });
      if (!res.todo) return;
      items = [res.todo, ...items];
      paint();
    } catch (err) {
      console.error("[todos] create failed", err);
      ctx.showToast("Couldn't add task — try again", "error");
    }
  }

  async function setDueDate(id: string, raw: string): Promise<void> {
    const t = items.find((x) => x.id === id);
    if (!t) return;
    const val = raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
    const prev = t.dueDate ?? null;
    if (prev === val) return;
    t.dueDate = val;
    paint();
    try {
      await api<{ ok: boolean }>(`/portal/todos/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: JSON.stringify({ dueDate: val }),
        headers: { "content-type": "application/json" },
      });
    } catch (err) {
      console.error("[todos] date update failed", err);
      t.dueDate = prev;
      paint();
      ctx.showToast("Couldn't save the date — try again", "error");
    }
  }

  function setScope(next: TodoScope): void {
    if (next === scope) return;
    scope = next;
    for (const b of scopeBtns) {
      const active = b.dataset["scope"] === next;
      b.classList.toggle("is-active", active);
      b.setAttribute("aria-selected", active ? "true" : "false");
    }
    hint.textContent = TODO_HINTS[next];
    input.placeholder = next === "team" ? "Add a task for the team…" : "Add a personal task…";
    listEl.innerHTML = `<li class="dash-empty" style="list-style:none;"><div class="skeleton skeleton--row" style="height:32px"></div><div class="skeleton skeleton--row" style="height:32px;width:82%"></div></li>`;
    void load();
  }

  for (const b of scopeBtns) {
    b.addEventListener("click", () => {
      const s = b.dataset["scope"];
      if (s === "team" || s === "personal") setScope(s);
    });
  }

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (!text) return;
    input.value = "";
    void add(text);
  });

  paint();
  void load();

  return {
    cleanup(): void {
      destroyed = true;
    },
  };
}


function renderStdLinks(host: HTMLElement, cats: StdLinkCategory[]): void {
  host.innerHTML = cats
    .map(
      (c) => `
        <details class="dash-std__cat">
          <summary class="dash-std__head">
            <span class="dash-std__lbl">${escapeHtml(c.label)} <span class="dash-std__count">${c.items.length}</span></span>
            <span class="dash-std__caret" aria-hidden="true">${ICO.caret}</span>
          </summary>
          <ul class="dash-std__list">
            ${c.items
              .map(
                (it) => `
              <li>
                <a class="dash-std__link" href="${escapeHtml(it.url)}" target="_blank" rel="noopener noreferrer">
                  <span class="dash-std__name">${escapeHtml(it.name)}</span>
                  <span class="dash-std__domain">${escapeHtml(it.domain)}</span>
                </a>
              </li>
            `,
              )
              .join("")}
          </ul>
        </details>
      `,
    )
    .join("");
}

// ── Google Reviews public refresh API ────────────────────────────────────────

let refreshGoogleReviewsFn: (() => void) | null = null;

export function refreshGoogleReviews(): void {
  refreshGoogleReviewsFn?.();
}

// ── Page ─────────────────────────────────────────────────────────────────────

const dashboardPage: Page = {
  name: "dashboard",
  label: "Dashboard",
  icon: "",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    let disposed = false;
    let controller: AbortController | null = null;
    let clockTimer: number | null = null;
    let weatherTimer: number | null = null;

    // Initial render with demo data so the page is interactive immediately.
    // Greeting addresses the signed-in user by first name when /portal/me
    // has populated it; falls back to the retailer's business name when
    // no personal name is available (per spec — first run, no DB linkage,
    // etc.).
    let view: DashView = demoView();
    const greetName = ctx.user?.firstName?.trim() || ctx.user?.retailerName;
    if (greetName) {
      view = { ...view, ownerName: greetName };
    }
    renderShell(container, view);

    // Kit morning-briefing widget — self-contained; fetches once on mount and
    // renders dismissible alert cards. Kept outside renderAll() so it never
    // churns and the rest of the dashboard's in-place updates are unaffected.
    let kitBarCleanup: (() => void) | null = null;
    let messagesWidgetCleanup: (() => void) | null = null;
    let activityWidgetCleanup: (() => void) | null = null;
    {
      const kitHost = container.querySelector<HTMLElement>("[data-role='kit-bar']");
      const kitRid = ctx.user?.retailerId;
      if (kitHost && kitRid) kitBarCleanup = mountKitBar(kitHost, kitRid);
      // Messages widget — self-contained: recent unread customer threads with an
      // inline chat popup. Owns its own fetch + realtime, so it's mounted here
      // (not driven by the aggregator/renderAll).
      const msgHost = container.querySelector<HTMLElement>("[data-role='messages']");
      if (msgHost && kitRid) messagesWidgetCleanup = mountMessagesWidget(msgHost, ctx, kitRid);
      // Recent Activity — self-contained PG feed + realtime.
      const actHost = container.querySelector<HTMLElement>("[data-role='activity']");
      if (actHost && kitRid) activityWidgetCleanup = mountActivityWidget(actHost, ctx, kitRid);
      // Floating action buttons (Kit / Message / Call) are now mounted at shell
      // level (shell/fab.ts → mountShellFab) so they persist on every route.
    }

    // ── Section roots ──────────────────────────────────────────────────────
    const $ = <T extends HTMLElement>(role: string): T =>
      container.querySelector<T>(`[data-role='${role}']`)!;

    const bannerEl = $<HTMLElement>("banner");
    const clockEl = $<HTMLElement>("clock-time");
    const greetTextEl = $<HTMLElement>("greet-text");
    const dateTextEl = $<HTMLElement>("date-text");

    const setBanner = (msg: string | null): void => {
      if (!msg) {
        bannerEl.hidden = true;
        bannerEl.textContent = "";
        return;
      }
      bannerEl.hidden = false;
      bannerEl.textContent = msg;
    };

    // (Social Media AI widget removed from this row 2026-05-28 — it lives at
    // its own /social-ai page; the slot is now Supplier News.)

    // Radio widget owns a subscription to the shell-level radio engine. The
    // cleanup fn is captured here so we can unhook it on page unmount.
    let radioCleanup: PageCleanup | null = null;

    // Quick Notes widget owns an in-memory note list + listeners. PG-backed
    // (migration 0011 + /api/portal/quick-notes); see mountQuickNotes below.
    let notesHandle: NotesHandle | null = null;

    // To-Do List widget — PG-backed with team/personal scope toggle.
    // Migration 0012 + /api/portal/todos. mountTodoList declared below.
    let todosHandle: TodosHandle | null = null;

    // Needs Attention snag list — PG-backed editable list with detail popup.
    // Migration 0013 + /api/portal/snags. mountSnagList declared below.
    let snagsHandle: SnagHandle | null = null;

    // Per-section render memoisation. The dashboard keeps a live data
    // connection (realtime WS, with a 30s polling fallback when the socket is
    // unreachable) and re-runs renderAll() whenever fresh data arrives. The
    // old renderAll re-ran EVERY render function on EVERY hydrate, replacing
    // each widget's innerHTML even when the data was byte-for-byte identical.
    // That full-DOM churn is what made the dashboard visibly "auto-refresh"/
    // jump — resetting scroll, focus and transient UI on every tick. We now
    // fingerprint each section's data slice and only re-render the widgets
    // whose data actually changed, so background updates (a new calendar
    // event, a pipeline move, an inbound message) land in place almost
    // instantly while everything untouched stays exactly where it is.
    // (Steve, 2026-05-31.)
    const sectionSig: Record<string, string> = {};
    let quickActionsRendered = false;
    const renderSection = (key: string, data: unknown, render: () => void): boolean => {
      const sig = JSON.stringify(data);
      if (sectionSig[key] === sig) return false;
      sectionSig[key] = sig;
      render();
      return true;
    };

    const renderAll = (v: DashView): void => {
      let changed = false;
      const mark = (did: boolean): void => { if (did) changed = true; };

      mark(renderSection("stats", v.cards, () => renderStats($("stats"), v, ctx)));
      // Quick actions are derived solely from ctx (a fixed button set) — mount
      // once and never re-render; re-rendering only re-flashed identical DOM.
      if (!quickActionsRendered) {
        renderQuickActions($("qa"), ctx);
        quickActionsRendered = true;
        changed = true;
      }
      mark(renderSection("pipeline", v.pipeline, () => renderPipeline($("pipeline-grid"), v.pipeline, ctx)));
      mark(renderSection("revenue", v.revenue, () => renderRevenue($("revenue"), v.revenue)));
      // Radio: mount once per page session and leave its DOM alone on
      // subsequent renderAll() calls. Re-running renderRadio every hydrate
      // wiped the widget DOM and re-registered listeners, which clobbered
      // transient UI state (in-progress search, hover, play-pulse) and
      // visibly flashed the widget every time. The audio element lives on
      // document.body so it survives router transitions independently;
      // the persistent radio session isn't affected by skipping a re-mount.
      // Mirrors the guard pattern already used by snags + social-ai below.
      if (!radioCleanup) {
        radioCleanup = renderRadio($("radio"), ctx);
      }
      // Messages are owned by the self-contained mountMessagesWidget (recent
      // unread threads + inline chat popup), not renderAll.
      mark(renderSection("schedule", v.schedule, () => renderSchedule($("schedule"), v.schedule, ctx)));
      // Recent Activity is self-managed (mountActivityWidget) — not renderAll.
      if (!snagsHandle) {
        snagsHandle = mountSnagList({
          listEl: $("attention"),
          addBtn: $<HTMLButtonElement>("snag-add"),
          ctx,
        });
      }
      void v.attention;
      mark(renderSection("couriers", v.couriers, () => renderCouriers($("couriers"), v.couriers)));
      // Offers also depend on the current supplier-scope toggle, so the
      // fingerprint folds it in alongside the offer list.
      mark(renderSection("offers", { offers: v.offers, scope: readSupplierScope() }, () => renderOffers($("offers"), v.offers, readSupplierScope())));
      mark(renderSection("news", v.news, () => renderNews($("news"), v.news, "mine")));
      mark(renderSection("reviews", v.reviews, () => renderReviews($("reviews"), v.reviews)));
      // My Links + Standard Links are self-managed (localStorage-backed) and
      // wired once in the mount block below — not driven by renderAll.

      // A freshly-rendered section may contain [data-perm] items (e.g. the
      // stat cards carry per-card permission keys). Role-gating is otherwise
      // only re-applied on navigation, so re-gate here whenever something
      // actually re-rendered — a background update must never flash a tile
      // the active role shouldn't see. No-op when nothing changed.
      if (changed) applyRoleSoon();
    };

    renderAll(view);
    setBanner(view.isDemo ? "Loading live dashboard data…" : null);

    // ── Header buttons ────────────────────────────────────────────────────
    // (Reset Layout button removed — drag-drop layout reorder is intentionally
    // gone from this build; nothing to reset.)
    // Hero search bar → Spotlight (same overlay as the top-bar magnifier).
    // Both focus and submit open it so a single click or Enter works.
    const heroSearchForm = $<HTMLFormElement>("search-form");
    const heroSearchInput = heroSearchForm.querySelector<HTMLInputElement>("input[type='search']");
    heroSearchForm.addEventListener("submit", (e) => {
      e.preventDefault();
      openSpotlight({ showToast: ctx.showToast });
    });
    heroSearchInput?.addEventListener("focus", () => {
      heroSearchInput.blur();
      openSpotlight({ showToast: ctx.showToast });
    });

    // ── Card-level "View all" links / etc. ────────────────────────────────
    $("pipeline-all").addEventListener("click", () => ctx.navigate("crm"));
    $("msg-all").addEventListener("click", () => ctx.navigate("messages"));
    $("sched-all").addEventListener("click", () => ctx.navigate("calendar"));
    $("activity-all").addEventListener("click", () => ctx.navigate("crm"));
    $("cv-add").addEventListener("click", () => {
      void (async () => {
        const tn = await promptDialog(
          "Paste tracking number (DPD, DHL, Royal Mail, Parcelforce, APC, or Evri):",
          { title: "Add tracking", placeholder: "Tracking number" },
        );
        if (!tn) return;
        const courier = detectCourier(tn);
        if (!courier) {
          await alertDialog(
            `Sorry — we couldn’t detect the courier for "${tn}". Supported: DPD, DHL, Royal Mail, Parcelforce, APC, Evri.`,
            { title: "Courier not detected" },
          );
          return;
        }
        const ref = (await promptDialog("Reference (e.g. KOS-8AEB5D12D1F5):", {
          title: "Reference",
          defaultValue: randomKosRef(),
        })) || "KOS-0000";
        const cust = (await promptDialog("Customer / supplier:", {
          title: "Who is it for?",
          defaultValue: "",
        })) || "--";
        view.couriers.unshift({
          id: "t" + Date.now(),
          ref,
          customer: cust,
          tracking: tn,
          status: "in_transit",
          etaDays: 2,
          note: "",
        });
        renderCouriers($("couriers"), view.couriers);
        await alertDialog(
          "Added — detected courier: " + COURIERS[courier].name,
          { title: "Tracking added" },
        );
      })();
    });
    const reviewsHost = $<HTMLElement>("reviews");
    reviewsHost.addEventListener("click", (e) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const btn = target.closest<HTMLButtonElement>("[data-role='gbp-post-update']");
      if (!btn) return;
      const gbp = (window as unknown as { kosGBPPostUpdate?: () => void }).kosGBPPostUpdate;
      if (typeof gbp === "function") gbp();
      else ctx.showToast("Post Update — coming soon", "info");
    });
    refreshGoogleReviewsFn = (): void => renderReviews(reviewsHost, view.reviews);

    // ── My Links — PG-backed (GET/POST/PUT/DELETE /api/portal/links) ──────
    {
      const host = $("my-links");
      let myList: StoredLink[] = [];
      const reload = async (): Promise<void> => {
        try {
          const resp = await ctx.api<{ links?: Array<{ id: string; label: string; url: string }> }>("/portal/links");
          myList = (resp.links ?? []).map((l) => ({ id: l.id, name: l.label, url: l.url }));
        } catch { myList = []; }
        renderMyLinks(host, myList, {
          onEdit: (id) => {
            const existing = myList.find((l) => l.id === id);
            if (!existing) return;
            openLinkEditor(existing, ({ name, url }) => void (async () => {
              try { await ctx.api(`/portal/links/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify({ label: name, url }) }); }
              catch { ctx.showToast("Couldn't save link", "error"); }
              await reload();
            })());
          },
          onDelete: (id) => void (async () => {
            try { await ctx.api(`/portal/links/${encodeURIComponent(id)}`, { method: "DELETE" }); }
            catch { ctx.showToast("Couldn't delete link", "error"); }
            await reload();
          })(),
        });
      };
      $("add-link").addEventListener("click", () => {
        openLinkEditor(null, ({ name, url }) => void (async () => {
          try { await ctx.api("/portal/links", { method: "POST", body: JSON.stringify({ label: name, url }) }); }
          catch { ctx.showToast("Couldn't add link", "error"); }
          await reload();
        })());
      });
      void (async () => { await migrateMyLinksFromLocalStorage(ctx.api); await reload(); })();
    }

    // ── Standard Links — PG-backed (GET/POST /api/portal/links/standard) ──
    {
      const host = $("std-links");
      const defaults = view.stdLinks;
      let stdCats: StdLinkCategory[] = defaults;
      const paint = (): void => renderStdLinks(host, stdCats);
      const reload = async (): Promise<void> => {
        try {
          const resp = await ctx.api<{ categories?: StdLinkCategory[] | null }>("/portal/links/standard");
          stdCats = Array.isArray(resp.categories) && resp.categories.length > 0 ? resp.categories : defaults;
        } catch { stdCats = defaults; }
        paint();
      };
      $("manage-links").addEventListener("click", () => {
        openStdLinksManager(stdCats, defaults, (next) => void (async () => {
          stdCats = next; paint();
          try { await ctx.api("/portal/links/standard", { method: "POST", body: JSON.stringify({ categories: next }) }); ctx.showToast("Links saved", "success"); }
          catch { ctx.showToast("Couldn't save links", "error"); }
        })());
      });
      void (async () => { await migrateStdLinksFromLocalStorage(ctx.api); await reload(); })();
    }

    // ── Supplier Offers: scope toggle + refresh + fetch ──────────────────
    const offersHost = $<HTMLElement>("offers");
    const offersCard = container.querySelector<HTMLElement>("#w-supplier-offers");
    const scopeBtns = offersCard
      ? Array.from(offersCard.querySelectorAll<HTMLButtonElement>(".kos-soft-seg button[data-scope]"))
      : [];

    const paintScopeButtons = (current: "mine" | "all"): void => {
      for (const b of scopeBtns) {
        const active = b.dataset["scope"] === current;
        b.classList.toggle("active", active);
        b.setAttribute("aria-selected", active ? "true" : "false");
      }
    };
    paintScopeButtons(readSupplierScope());

    for (const b of scopeBtns) {
      b.addEventListener("click", () => {
        const scope = (b.dataset["scope"] === "all" ? "all" : "mine") as "mine" | "all";
        writeSupplierScope(scope);
        paintScopeButtons(scope);
        renderOffers(offersHost, view.offers, scope);
      });
    }

    offersHost.addEventListener("click", (e) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // CTA button: claim/open external link (keeps existing behaviour).
      const cta = target.closest<HTMLButtonElement>("[data-role='offer-cta']");
      if (cta) {
        e.stopPropagation(); // don't also open the popup behind it
        const id = cta.dataset["offerId"] || "";
        const offer = view.offers.find((o) => o.id === id);
        if (!offer) return;
        if (offer.ctaUrl) {
          window.open(offer.ctaUrl, "_blank", "noopener");
        } else {
          ctx.showToast(`${offer.cta} request sent to ${offer.supplierName}`, "success");
        }
        const retailerId = ctx.user?.retailerId;
        if (retailerId) {
          ctx.api(`/supplier-offers/${encodeURIComponent(offer.id)}/claim`, {
            method: "POST",
            body: JSON.stringify({
              retailerId,
              retailerName: ctx.user?.retailerName ?? "",
            }),
          }).catch(() => { /* best effort */ });
        }
        return;
      }
      const connect = target.closest<HTMLAnchorElement>("[data-role='offers-connect-more']");
      if (connect) {
        e.preventDefault();
        ctx.navigate("settings");
        return;
      }
      // Row click → open full offer overlay (retailer stays on dashboard).
      const row = target.closest<HTMLElement>("[data-role='offer-open']");
      if (row) {
        const id = row.dataset["offerId"] || "";
        const offer = view.offers.find((o) => o.id === id);
        if (!offer) return;
        openSupplierContentPopup({
          supplierName: offer.supplierName,
          supplierColor: offer.supplierColor,
          headline: offer.title,
          subtitle: formatExpires(offer.validUntil),
          html: offer.contentHtml || `<p>${escapeHtml(offer.description || "")}</p>`,
        });
      }
    });

    const fetchOffers = async (): Promise<void> => {
      const retailerId = ctx.user?.retailerId;
      const qs = retailerId ? `?retailerId=${encodeURIComponent(retailerId)}` : "";
      try {
        const resp = await ctx.api<{ ok?: boolean; offers?: unknown }>(
          `/supplier-offers${qs}`,
        );
        if (disposed) return;
        const list = Array.isArray(resp.offers) ? (resp.offers as Record<string, unknown>[]) : [];
        view.offers = list.map((o) => ({
          id: String(o["id"] ?? ""),
          supplierId: String(o["supplierId"] ?? ""),
          supplierName: String(o["supplierName"] ?? "Supplier"),
          supplierColor: String(o["supplierColor"] ?? "#3b82f6"),
          type: (o["type"] === "new-customer" ? "new-customer" : "account-holder"),
          title: String(o["title"] ?? ""),
          description: String(o["description"] ?? ""),
          contentHtml: String(o["contentHtml"] ?? o["description"] ?? ""),
          badge: String(o["badge"] ?? ""),
          cta: String(o["cta"] ?? ""),
          ctaUrl: String(o["ctaUrl"] ?? ""),
          validUntil: (o["validUntil"] as string | null | undefined) ?? null,
        }));
        renderOffers(offersHost, view.offers, readSupplierScope());
      } catch {
        if (disposed) return;
        view.offers = [];
        renderOffers(offersHost, view.offers, readSupplierScope());
      }
    };

    const offersRefreshBtn = $<HTMLButtonElement>("offers-refresh");
    offersRefreshBtn.addEventListener("click", () => { void fetchOffers(); });
    void fetchOffers();

    // ── Supplier News: scope toggle + refresh + fetch ─────────────────────
    const newsHost = $<HTMLElement>("news");
    // Supplier News is intentionally connected-only: no scope toggle in the
    // widget. Render always uses scope='mine' so it filters to suppliers the
    // retailer has onboarded.

    newsHost.addEventListener("click", (e) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const row = target.closest<HTMLElement>("[data-role='news-open']");
      if (!row) return;
      const id = row.dataset["newsId"] || "";
      const item = view.news.find((n) => n.id === id);
      if (!item) return;
      const dateStr = item.publishedAt
        ? new Date(item.publishedAt).toLocaleDateString("en-GB", {
            day: "numeric", month: "long", year: "numeric",
          })
        : "";
      openSupplierContentPopup({
        supplierName: item.supplierName,
        supplierColor: item.supplierColor,
        headline: item.headline,
        subtitle: dateStr,
        html: item.contentHtml || `<p>${escapeHtml(item.summary || "")}</p>`,
      });
    });

    const fetchNews = async (): Promise<void> => {
      const retailerId = ctx.user?.retailerId;
      if (!retailerId) {
        view.news = [];
        renderNews(newsHost, view.news, "mine");
        return;
      }
      try {
        const resp = await ctx.api<{ ok?: boolean; items?: unknown }>(
          `/supplier-offers-news/feed/${encodeURIComponent(retailerId)}`,
        );
        if (disposed) return;
        const list = Array.isArray(resp.items) ? (resp.items as Record<string, unknown>[]) : [];
        view.news = list
          .filter((it) => (it["kind"] ?? "news") === "news")
          .map((it): SupplierNewsItem => ({
            id: String(it["id"] ?? ""),
            supplierId: String(it["supplierId"] ?? ""),
            supplierName: String(it["supplierName"] ?? "Supplier"),
            supplierColor: String(it["supplierColor"] ?? "#3b82f6"),
            headline: String(it["headline"] ?? ""),
            summary: String(it["summary"] ?? ""),
            contentHtml: String(it["contentHtml"] ?? ""),
            thumbnail: (it["thumbnail"] as string | null | undefined) ?? null,
            publishedAt: typeof it["publishedAt"] === "number"
              ? (it["publishedAt"] as number)
              : null,
          }));
        renderNews(newsHost, view.news, "mine");
      } catch {
        if (disposed) return;
        view.news = [];
        renderNews(newsHost, view.news, "mine");
      }
    };

    const newsRefreshBtn = $<HTMLButtonElement>("news-refresh");
    newsRefreshBtn.addEventListener("click", () => { void fetchNews(); });
    void fetchNews();

    // ── Calendar widget (page-local; fetches live events per month) ───────
    // Per Item 13: month grid + events listed below the selected date,
    // + Add event jumps to the full calendar (pre-filled date), Full
    // calendar link top-right. Clicking a day in the grid updates the
    // day-list below.
    const _today = new Date();
    let calCursor: CalendarCursor = { year: _today.getFullYear(), month: _today.getMonth() };
    let calSelected = todayYMDLocal();
    let calEvents: Map<string, MiniCalEvent[]> = new Map();
    let calFetchSeq = 0;
    const calLabelEl = $<HTMLElement>("cal-month-label");
    const calGridEl = $<HTMLElement>("cal-grid");
    const calDayListTitleEl = $<HTMLElement>("cal-daylist-title");
    const calDayListItemsEl = $<HTMLElement>("cal-daylist-items");

    const drawCalendar = (): void => {
      renderCalendarWidget(calLabelEl, calGridEl, calCursor, calEvents, calSelected, (ymd) => {
        calSelected = ymd;
        drawDayList();
        // Re-render grid in place so the selected cell updates.
        renderCalendarWidget(calLabelEl, calGridEl, calCursor, calEvents, calSelected, (ymd2) => {
          calSelected = ymd2;
          drawDayList();
          drawCalendar();
        });
      });
    };

    const drawDayList = (): void => {
      const events = calEvents.get(calSelected) ?? [];
      const isToday = calSelected === todayYMDLocal();
      const parsedDate = new Date(calSelected + "T00:00:00");
      const dateLabel = parsedDate.toLocaleDateString("en-GB", {
        weekday: "long", day: "numeric", month: "long",
      });
      calDayListTitleEl.textContent = isToday ? `Today — ${dateLabel}` : dateLabel;
      if (events.length === 0) {
        calDayListItemsEl.innerHTML = `<li class="dash-cal-daylist__empty">No events on this date.</li>`;
        return;
      }
      calDayListItemsEl.innerHTML = events
        .map((e, i) => `
          <li class="dash-cal-daylist__row" data-i="${i}" tabindex="0" role="button" aria-label="Open event details">
            <span class="dash-cal-daylist__bar" style="background:${miniCalColor(e.type)}"></span>
            <span class="dash-cal-daylist__title">${escapeHtml(e.title)}</span>
          </li>
        `)
        .join("");

      // Click a row → open the shared event-detail popup (same UX as the
      // main calendar page). Save/delete here calls /api/portal/calendar
      // directly (no offline-first repo on the dashboard); on success we
      // re-fetch the month so the grid + day list reflect changes.
      for (const row of calDayListItemsEl.querySelectorAll<HTMLElement>("[data-i]")) {
        const idx = Number(row.dataset["i"]);
        const ev = events[idx];
        if (!ev) continue;
        const open = (): void => {
          openEventDetail(
            {
              id: ev.id,
              title: ev.title,
              type: ev.type,
              date: ev.date,
              time: ev.time,
              endTime: ev.endTime,
              allDay: ev.allDay,
              notes: ev.notes,
              customer: ev.customer,
              recurrence: ev.recurrence,
              // Mini-cal only fetches user-created PG rows; nothing here
              // is derived. If derived events ever start flowing through
              // /portal/calendar, flag them at the fetch step.
              derived: false,
              readOnly: false,
              masterId: null,
            },
            {
              showToast: ctx.showToast,
              onAfterSave: () => { void refreshCalendar(); },
              onAfterDelete: () => { void refreshCalendar(); },
            },
          );
        };
        row.addEventListener("click", open);
        row.addEventListener("keydown", (e) => {
          const ke = e as KeyboardEvent;
          if (ke.key === "Enter" || ke.key === " ") {
            ke.preventDefault();
            open();
          }
        });
      }
    };

    const refreshCalendar = async (): Promise<void> => {
      const mySeq = ++calFetchSeq;
      drawCalendar();
      drawDayList();
      const next = await fetchMonthEvents(calCursor);
      if (mySeq !== calFetchSeq) return;
      calEvents = next;
      drawCalendar();
      drawDayList();
    };
    void refreshCalendar();
    $<HTMLButtonElement>("cal-prev").addEventListener("click", () => {
      calCursor = stepCursor(calCursor, -1);
      calEvents = new Map();
      void refreshCalendar();
    });
    $<HTMLButtonElement>("cal-next").addEventListener("click", () => {
      calCursor = stepCursor(calCursor, 1);
      calEvents = new Map();
      void refreshCalendar();
    });
    $<HTMLButtonElement>("cal-add").addEventListener("click", () => {
      // Hand off to the full calendar page with the selected date pre-set.
      // The calendar page's `+ Add event` button uses `selected` as the
      // default — so navigating here lands the user in the right context.
      ctx.navigate("calendar", { date: calSelected, add: "1" });
    });
    $<HTMLButtonElement>("cal-fullcal").addEventListener("click", () => {
      ctx.navigate("calendar", { date: calSelected });
    });

    // ── News feed (stub — admin announcements TBD) ────────────────────────
    const newsList = $<HTMLElement>("news");
    newsList.innerHTML = `<li class="dash-news-list__empty">No platform announcements right now.</li>`;

    // ── Quick Notes (PG-backed; per-user) ─────────────────────────────────
    const notesListEl = $<HTMLElement>("notes-list");
    const notesAddBtn = $<HTMLButtonElement>("notes-add");
    notesHandle = mountQuickNotes(notesListEl, notesAddBtn, ctx);

    // ── To-Do List (PG-backed; team/personal scopes) ──────────────────────
    todosHandle = mountTodoList({
      listEl: $<HTMLElement>("todos"),
      form: $<HTMLFormElement>("todo-form"),
      input: $<HTMLInputElement>("todo-input"),
      hint: $<HTMLElement>("todo-hint"),
      scopeBtns: Array.from(
        document.querySelectorAll<HTMLButtonElement>("[data-role='todo-scope']"),
      ),
      ctx,
    });

    // ── Live clock + date ─────────────────────────────────────────────────
    const tickClock = (): void => {
      const now = new Date();
      clockEl.textContent = clockHHMMSS(now);
      // Re-paint date + greeting (handles tab being open across midnight).
      dateTextEl.textContent = DATE_FMT.format(now);
      const g = greetingFor(now);
      if (greetTextEl.textContent !== g) greetTextEl.textContent = g;
    };
    tickClock();

    const startClock = (): void => {
      if (clockTimer !== null) return;
      clockTimer = window.setInterval(tickClock, 1000);
    };
    const stopClock = (): void => {
      if (clockTimer !== null) {
        window.clearInterval(clockTimer);
        clockTimer = null;
      }
    };
    startClock();

    const onVisibility = (): void => {
      if (document.hidden) {
        stopClock();
      } else {
        tickClock();
        startClock();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // ── Weather ───────────────────────────────────────────────────────────
    const weatherIconEl = $<HTMLElement>("weather-icon");
    const weatherTempEl = $<HTMLElement>("weather-temp");
    const weatherCondEl = $<HTMLElement>("weather-cond");
    const loadWeather = (): void => {
      fetch("/api/weather", { credentials: "same-origin" })
        .then((r) => r.json() as Promise<WeatherResponse>)
        .then((d) => {
          if (disposed) return;
          weatherTempEl.textContent = String(Math.round(d.current.tempC));
          weatherCondEl.textContent = d.current.condition;
          weatherIconEl.innerHTML = weatherSvg(d.current.icon);
        })
        .catch(() => {});
    };
    loadWeather();
    weatherTimer = window.setInterval(loadWeather, 10 * 60 * 1000);

    // ── Best-effort backend hydrate ───────────────────────────────────────
    const hydrate = async (): Promise<void> => {
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;
      try {
        const { view: v, failedSections, hardFail } = await loadView(ctx, signal);
        if (disposed || signal.aborted) return;
        view = { ...v, ownerName: view.ownerName };
        renderAll(view);
        if (hardFail) {
          setBanner("Showing demo dashboard data — live data unavailable.");
        } else if (failedSections.length > 0) {
          setBanner(
            `Partial live data — ${failedSections.length} of 7 sections unavailable, showing demo for those.`,
          );
        } else {
          setBanner(null);
        }
      } catch (err) {
        if (signal.aborted) return;
        // Already rendering demo; just toast for the operator.
        const msg =
          err instanceof ApiError
            ? `Couldn't refresh dashboard (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Couldn't refresh dashboard";
        ctx.showToast(msg, "error");
      }
    };
    void hydrate();

    // ── Realtime: subscribe to contact.updated ────────────────────────────
    // When a contact moves pipeline stage anywhere in the platform (CRM
    // page, quote builder accept, customer record), the API broadcasts
    // `contact.updated` to all this retailer's portal sessions. We
    // re-hydrate the dashboard so the pipeline counts reflect the new
    // distribution. Re-hydrate is debounced (200ms) to coalesce bursts
    // (e.g. a quote-create that moves the contact lead→quoted plus
    // creates a quote row will fire two events in quick succession).
    let realtimeRefreshTimer: number | null = null;
    const scheduleRealtimeRefresh = (): void => {
      if (realtimeRefreshTimer !== null) return;
      realtimeRefreshTimer = window.setTimeout(() => {
        realtimeRefreshTimer = null;
        if (!disposed) void hydrate();
      }, 200);
    };
    const offContactUpdated = realtime.onEvent("contact.updated", () => {
      scheduleRealtimeRefresh();
    });
    // Same debounced rehydrate for every other retailer-scoped event the
    // dashboard cares about — quote create/send/accept moves the pipeline
    // + revenue tiles, an inbound message bumps the messages widget +
    // unread counts, a delivery status flip updates the courier tile,
    // a payment moves revenue + activity. Radio is untouched (it lives in
    // its own host and survives renderAll). Polling fallback is unchanged
    // — `onPollTick` already runs `hydrate()` periodically when WS is down.
    const offQuoteCreated   = realtime.onEvent("quote.created",   () => { scheduleRealtimeRefresh(); });
    const offQuoteUpdated   = realtime.onEvent("quote.updated",   () => { scheduleRealtimeRefresh(); });
    const offMessageRcvd    = realtime.onEvent("message.received",() => { scheduleRealtimeRefresh(); });
    const offDeliveryUpdate = realtime.onEvent("delivery.updated",() => { scheduleRealtimeRefresh(); });
    const offPaymentRcvd    = realtime.onEvent("payment.received",() => { scheduleRealtimeRefresh(); });
    const offOrderUpdated   = realtime.onEvent("order.updated",   () => { scheduleRealtimeRefresh(); });
    // A customer booking a visit broadcasts `appointment.requested`; re-hydrate
    // so the Schedule widget + "events" stat tile reflect the new pending
    // appointment instantly instead of waiting for the 30s polling fallback.
    const offApptRequested  = realtime.onEvent("appointment.requested", () => { scheduleRealtimeRefresh(); });
    // A retailer accept/decline/dismiss broadcasts `appointment.actioned`;
    // re-hydrate so the Schedule widget + "events" tile stay in sync across
    // this retailer's other open sessions.
    const offApptActioned   = realtime.onEvent("appointment.actioned", () => { scheduleRealtimeRefresh(); });
    // A customer confirming a pending appointment broadcasts `booking.updated`;
    // re-hydrate so the Schedule widget reflects the confirmed booking. (Issue 2.)
    const offBookingUpdated = realtime.onEvent("booking.updated", () => { scheduleRealtimeRefresh(); });
    // A calendar event created/edited/removed (including via Kit) broadcasts
    // `calendar.updated`; re-hydrate so the "Today's Events" tile reflects the
    // change instantly instead of staying stale until the next mount. (Kit-
    // created events POST through /portal/calendar, which now broadcasts.)
    const offCalendarUpdated = realtime.onEvent("calendar.updated", () => { scheduleRealtimeRefresh(); });
    // Polling-fallback companion: if the WS goes down and the client
    // kicks over to slow-poll mode, fire a hydrate on every tick so the
    // dashboard still gets fresh data.
    const offPollTick = realtime.onPollTick(() => {
      if (!disposed) void hydrate();
    });

    // ── Cleanup ───────────────────────────────────────────────────────────
    return (): void => {
      disposed = true;
      stopClock();
      if (weatherTimer !== null) {
        window.clearInterval(weatherTimer);
        weatherTimer = null;
      }
      if (realtimeRefreshTimer !== null) {
        window.clearTimeout(realtimeRefreshTimer);
        realtimeRefreshTimer = null;
      }
      offContactUpdated();
      offQuoteCreated();
      offQuoteUpdated();
      offMessageRcvd();
      offDeliveryUpdate();
      offPaymentRcvd();
      offOrderUpdated();
      offApptRequested();
      offApptActioned();
      offBookingUpdated();
      offCalendarUpdated();
      offPollTick();
      document.removeEventListener("visibilitychange", onVisibility);
      controller?.abort();
      controller = null;
      radioCleanup?.();
      radioCleanup = null;
      kitBarCleanup?.();
      kitBarCleanup = null;
      messagesWidgetCleanup?.();
      messagesWidgetCleanup = null;
      activityWidgetCleanup?.();
      activityWidgetCleanup = null;
      notesHandle?.cleanup();
      notesHandle = null;
      todosHandle?.cleanup();
      todosHandle = null;
      snagsHandle?.cleanup();
      snagsHandle = null;
      refreshGoogleReviewsFn = null;
    };
  },
};

registerPage(dashboardPage);

export default dashboardPage;
